// frontend/src/components/GuiasEntrega.js
// =============================================
// GUIAS DE ENTREGA - DOCUMENTO LOGÍSTICO (SEM VALORES)
// =============================================
// 🔥 CORRIGIDO: states individuais de loading por ação
//    (loadingPDF, loadingPrint, loadingApagarComps, etc.)
//    para que cada botão mostre o seu próprio estado
//    independentemente dos outros.
// =============================================
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import './Recibos.css';
import './GuiasEntrega.css';
import { Icon } from './SharedComponents';

const API_BASE_URL = '/api' || 'http://localhost:3000';
const ITEMS_PER_PAGE = 10;
const SEARCH_ITEMS_PER_PAGE = 5;
const MAX_COMPLEMENTARES = 3;

const STATUS = [
    { value: 'Emitida', label: 'Emitida', color: '#4f46e5', icon: 'description' },
    { value: 'Entregue', label: 'Entregue', color: '#10b981', icon: 'delivery_truck' },
    { value: 'Devolvida', label: 'Devolvida', color: '#f59e0b', icon: 'undo' },
    { value: 'Anulada', label: 'Anulada', color: '#dc2626', icon: 'cancel' }
];

function getStatusInfo(status) {
    return STATUS.find(s => s.value === status) || STATUS[0];
}

// =============================================
// HELPERS DE CLIENTE
// =============================================
const getClienteNome = (guia) => {
    if (!guia) return '—';
    return guia.clienteInfo?.nome
        || guia.faturaSnapshot?.cliente?.nome
        || guia.clienteId?.nome
        || '—';
};

const getClienteNuit = (guia) => {
    if (!guia) return '';
    const candidatos = [
        guia.clienteInfo?.nuit,
        guia.clienteInfo?.nif,
        guia.faturaSnapshot?.cliente?.nuit,
        guia.faturaSnapshot?.cliente?.nif,
        guia.clienteId?.nuit,
        guia.clienteId?.nif
    ];
    for (const c of candidatos) {
        if (c && String(c).trim()) return String(c).trim();
    }
    return '';
};

const getClienteEndereco = (guia) => {
    if (!guia) return '';
    const candidatos = [
        guia.clienteInfo?.endereco,
        guia.faturaSnapshot?.cliente?.endereco
    ];
    for (const c of candidatos) {
        if (c && String(c).trim()) return String(c).trim();
    }
    const m = guia.clienteId?.morada || {};
    const partes = [
        guia.clienteId?.endereco,
        m.rua, m.numero, m.bairro,
        m.cidade, m.provincia, m.pais
    ].filter(Boolean);
    return partes.join(', ').trim();
};

const getClienteTelefone = (guia) => {
    if (!guia) return '';
    return guia.clienteInfo?.telefone
        || guia.faturaSnapshot?.cliente?.telefone
        || guia.clienteId?.telefone
        || guia.contactoEntrega
        || '';
};

const getClienteEmail = (guia) => {
    if (!guia) return '';
    return guia.clienteInfo?.email
        || guia.faturaSnapshot?.cliente?.email
        || guia.clienteId?.email
        || '';
};

// =============================================
// TOAST
// =============================================
function Toast({ message, type, onClose }) {
    useEffect(() => {
        if (!message) return;
        const t = setTimeout(onClose, 4000);
        return () => clearTimeout(t);
    }, [message, onClose]);

    if (!message) return null;
    const colors = { success: '#10b981', danger: '#ef4444', warning: '#f59e0b', info: '#3b82f6' };
    const icons = { success: 'check_circle', danger: 'error', warning: 'warning', info: 'info' };

    return (
        <div className="toast-container" style={{
            position: 'fixed', bottom: '20px', right: '20px', zIndex: 99999,
            display: 'flex', flexDirection: 'column', gap: '8px',
            maxWidth: '400px', width: '100%', pointerEvents: 'none'
        }}>
            <div className={`toast-message toast-${type}`} style={{
                display: 'flex', alignItems: 'center', gap: '10px',
                padding: '12px 16px', background: '#ffffff', borderRadius: '12px',
                boxShadow: '0 10px 40px rgba(0,0,0,0.15), 0 4px 12px rgba(0,0,0,0.05)',
                borderLeft: `4px solid ${colors[type] || '#6b7280'}`,
                pointerEvents: 'auto', animation: 'slideInRight 0.3s ease-out'
            }}>
                <Icon name={icons[type] || 'info'} size={20} style={{ color: colors[type], flexShrink: 0 }} />
                <span style={{ flex: 1, fontSize: '0.875rem', color: '#1a1a2e', fontWeight: 500, lineHeight: 1.4 }}>
                    {message}
                </span>
                <button onClick={onClose} style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: '#9ca3af', padding: '4px', display: 'flex',
                    alignItems: 'center', justifyContent: 'center',
                    borderRadius: '50%', transition: 'all 0.2s ease', flexShrink: 0
                }}>
                    <Icon name="close" size={16} />
                </button>
            </div>
            <style>{`
                @keyframes slideInRight {
                    from { opacity: 0; transform: translateX(40px); }
                    to   { opacity: 1; transform: translateX(0); }
                }
            `}</style>
        </div>
    );
}

// =============================================
// PAGINAÇÃO DE ITENS
// =============================================
function ItensPagination({ currentPage, totalPages, onPageChange }) {
    if (totalPages <= 1) return null;

    const getPageNumbers = () => {
        const pages = [];
        if (totalPages <= 5) { for (let i = 1; i <= totalPages; i++) pages.push(i); return pages; }
        pages.push(1);
        let startPage = Math.max(2, currentPage - 1);
        let endPage = Math.min(totalPages - 1, currentPage + 1);
        if (currentPage <= 3) { startPage = 2; endPage = Math.min(totalPages - 1, 4); }
        if (currentPage >= totalPages - 2) { startPage = Math.max(2, totalPages - 3); endPage = totalPages - 1; }
        if (startPage > 2) pages.push('...');
        for (let i = startPage; i <= endPage; i++) pages.push(i);
        if (endPage < totalPages - 1) pages.push('...');
        pages.push(totalPages);
        return pages;
    };

    return (
        <div className="itens-pagination" style={{
            display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
            gap: '0.25rem', padding: '0.5rem 0.5rem 0.25rem 0.5rem',
            marginTop: '0.25rem', borderTop: '1px solid #e5e7eb'
        }}>
            <button className="pagination-btn" onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1}
                style={{
                    width: '26px', height: '26px', border: '1px solid #e5e7eb',
                    borderRadius: '50%', background: 'transparent', color: '#1a1a2e',
                    cursor: 'pointer', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', opacity: currentPage === 1 ? 0.4 : 1
                }}>
                <Icon name="chevron_left" size={14} />
            </button>
            {getPageNumbers().map((p, index) => p === '...'
                ? <span key={`dots-${index}`} className="pagination-dots">…</span>
                : <button key={p} className={`pagination-number ${p === currentPage ? 'active' : ''}`}
                    onClick={() => onPageChange(p)}>{p}</button>
            )}
            <button className="pagination-btn" onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                style={{
                    width: '26px', height: '26px', border: '1px solid #e5e7eb',
                    borderRadius: '50%', background: 'transparent', color: '#1a1a2e',
                    cursor: 'pointer', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', opacity: currentPage === totalPages ? 0.4 : 1
                }}>
                <Icon name="chevron_right" size={14} />
            </button>
        </div>
    );
}

// =============================================
// 🔥 GRID DE ITENS REMOVIDOS — 6 colunas × 1 linha
// Com botão + para devolver (total ou parcial)
// =============================================
const REMOVIDOS_COLUNAS = 6;
const REMOVIDOS_LINHAS = 1;
const REMOVIDOS_POR_PAGINA = REMOVIDOS_COLUNAS * REMOVIDOS_LINHAS;

function RemovidosGrid({ itens = [], title = 'Itens Removidos', onDevolver }) {
    const [page, setPage] = useState(1);
    const [devolverPrompt, setDevolverPrompt] = useState(null);

    if (!itens.length) return null;

    const totalPages = Math.ceil(itens.length / REMOVIDOS_POR_PAGINA);
    const startIndex = (page - 1) * REMOVIDOS_POR_PAGINA;
    const pageItems = itens.slice(startIndex, startIndex + REMOVIDOS_POR_PAGINA);
    const slots = Array.from({ length: REMOVIDOS_POR_PAGINA }).map((_, i) => pageItems[i] || null);

    const goPrev = () => setPage(p => Math.max(1, p - 1));
    const goNext = () => setPage(p => Math.min(totalPages, p + 1));

    const abrirDevolver = (it, globalIdx) => {
        if (!onDevolver) return;
        setDevolverPrompt({
            index: globalIdx,
            item: it,
            quantidade: it.quantidade || 0
        });
    };

    const confirmarDevolucao = () => {
        if (!devolverPrompt || !onDevolver) return;
        const q = Math.max(0, Math.min(
            parseFloat(devolverPrompt.quantidade) || 0,
            parseFloat(devolverPrompt.item.quantidade) || 0
        ));
        if (q <= 0) { setDevolverPrompt(null); return; }
        onDevolver(devolverPrompt.index, q);
        setDevolverPrompt(null);
    };

    return (
        <>
            <div style={{
                marginTop: '0.75rem',
                padding: '0.75rem 0.9rem',
                background: '#fffbeb',
                border: '1px solid rgba(245, 158, 11, 0.3)',
                borderLeft: '3px solid #f59e0b',
                borderRadius: '8px',
                fontSize: '0.55rem',
                color: '#92400e'
            }}>
                <div style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    gap: '0.5rem', marginBottom: '0.6rem', flexWrap: 'wrap'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 700 }}>
                        <Icon name="warning" size={14} />
                        <span>{title} ({itens.length})</span>
                    </div>

                    {totalPages > 1 && (
                        <div style={{
                            display: 'flex', alignItems: 'center',
                            gap: '0.35rem', fontSize: '0.6rem', fontWeight: 600
                        }}>
                            <button type="button" onClick={goPrev} disabled={page === 1}
                                style={{
                                    width: '22px', height: '22px',
                                    border: '1px solid rgba(245, 158, 11, 0.4)',
                                    borderRadius: '50%', background: 'transparent',
                                    color: '#d97706',
                                    cursor: page === 1 ? 'not-allowed' : 'pointer',
                                    opacity: page === 1 ? 0.4 : 1,
                                    display: 'flex', alignItems: 'center',
                                    justifyContent: 'center', padding: 0
                                }}>
                                <Icon name="chevron_left" size={11} />
                            </button>
                            <span style={{ color: '#d97706', minWidth: '60px', textAlign: 'center' }}>
                                Pág. {page} / {totalPages}
                            </span>
                            <button type="button" onClick={goNext} disabled={page === totalPages}
                                style={{
                                    width: '22px', height: '22px',
                                    border: '1px solid rgba(245, 158, 11, 0.4)',
                                    borderRadius: '50%', background: 'transparent',
                                    color: '#d97706',
                                    cursor: page === totalPages ? 'not-allowed' : 'pointer',
                                    opacity: page === totalPages ? 0.4 : 1,
                                    display: 'flex', alignItems: 'center',
                                    justifyContent: 'center', padding: 0
                                }}>
                                <Icon name="chevron_right" size={11} />
                            </button>
                        </div>
                    )}
                </div>

                <div style={{ color: '#78350f', marginBottom: '0.6rem', fontSize: '0.72rem' }}>
                    Estes itens vão para uma <strong>guia complementar</strong> que deve ser gerada após guardar.
                    {onDevolver && <span> Clica em <strong>+</strong> para devolver à guia ativa.</span>}
                </div>

                <div style={{
                    display: 'grid',
                    gridTemplateColumns: `repeat(${REMOVIDOS_COLUNAS}, minmax(0, 1fr))`,
                    gap: '0.4rem 0.6rem'
                }}>
                    {slots.map((it, idx) => {
                        const globalIdx = startIndex + idx;
                        return (
                            <div
                                key={idx}
                                style={{
                                    position: 'relative',
                                    padding: '0.3rem 0.5rem',
                                    background: it ? 'rgba(255, 255, 255, 0.6)' : 'transparent',
                                    border: it ? '1px solid rgba(245, 158, 11, 0.25)' : '1px dashed transparent',
                                    borderRadius: '6px',
                                    fontSize: '0.48rem',
                                    color: it ? '#78350f' : 'transparent',
                                    minHeight: '40px',
                                    display: 'flex', flexDirection: 'column',
                                    justifyContent: 'center', overflow: 'hidden'
                                }}
                            >
                                {it ? (
                                    <>
                                        <div style={{
                                            fontWeight: 600, whiteSpace: 'nowrap',
                                            overflow: 'hidden', textOverflow: 'ellipsis',
                                            paddingRight: onDevolver ? '18px' : 0
                                        }}>
                                            {it.nomeItem}
                                        </div>
                                        <div style={{ fontSize: '0.4rem', color: '#a16207' }}>
                                            {it.quantidade} {it.unidadeMedida || 'UN'}
                                            {it.codigoItem && (
                                                <span style={{ marginLeft: '4px', color: '#b45309' }}>
                                                    ({it.codigoItem})
                                                </span>
                                            )}
                                        </div>

                                        {onDevolver && (
                                            <button
                                                type="button"
                                                onClick={() => abrirDevolver(it, globalIdx)}
                                                title="Devolver item"
                                                style={{
                                                    position: 'absolute',
                                                    top: '3px', right: '3px',
                                                    width: '16px', height: '16px',
                                                    border: '1px solid #059669',
                                                    borderRadius: '50%',
                                                    background: 'rgba(5, 150, 105, 0.1)',
                                                    color: '#059669',
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    padding: 0,
                                                    fontSize: '0.55rem',
                                                    fontWeight: 800,
                                                    lineHeight: 1
                                                }}
                                            >
                                                +
                                            </button>
                                        )}
                                    </>
                                ) : (
                                    <span>&nbsp;</span>
                                )}
                            </div>
                        );
                    })}
                </div>

                {itens.length > REMOVIDOS_POR_PAGINA && (
                    <div style={{
                        marginTop: '0.5rem',
                        fontSize: '0.42rem',
                        color: '#a16207',
                        textAlign: 'right'
                    }}>
                        A mostrar {startIndex + 1}-{Math.min(startIndex + REMOVIDOS_POR_PAGINA, itens.length)} de {itens.length}
                    </div>
                )}
            </div>

            {devolverPrompt && (
                <div style={{
                    position: 'fixed', inset: 0, zIndex: 999999,
                    background: 'rgba(0,0,0,0.5)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                }} onClick={() => setDevolverPrompt(null)}>
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            background: '#fff', borderRadius: '12px',
                            padding: '1rem 1.25rem', maxWidth: '380px', width: '90%',
                            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
                        }}
                    >
                        <h4 style={{
                            margin: '0 0 0.5rem 0', fontSize: '0.9rem',
                            fontWeight: 700, color: '#1a1a2e'
                        }}>
                            Devolver item
                        </h4>
                        <p style={{ margin: '0 0 0.75rem 0', fontSize: '0.75rem', color: '#6b7280' }}>
                            <strong>{devolverPrompt.item.nomeItem}</strong><br />
                            Removido: {devolverPrompt.item.quantidade} {devolverPrompt.item.unidadeMedida || 'UN'}
                        </p>

                        <label style={{
                            fontSize: '0.7rem', fontWeight: 600, color: '#4a5568',
                            display: 'block', marginBottom: '4px'
                        }}>
                            Quantidade a devolver:
                        </label>
                        <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', marginBottom: '0.75rem' }}>
                            <input
                                type="number"
                                min="0"
                                max={devolverPrompt.item.quantidade}
                                step="1"
                                value={devolverPrompt.quantidade}
                                onChange={(e) => setDevolverPrompt(prev => ({
                                    ...prev,
                                    quantidade: parseFloat(e.target.value) || 0
                                }))}
                                autoFocus
                                style={{
                                    flex: 1, padding: '0.5rem 0.7rem',
                                    border: '1.5px solid #e5e7eb',
                                    borderRadius: '8px', fontSize: '0.85rem',
                                    outline: 'none', boxSizing: 'border-box'
                                }}
                            />
                            <button
                                type="button"
                                onClick={() => setDevolverPrompt(prev => ({
                                    ...prev,
                                    quantidade: prev.item.quantidade
                                }))}
                                style={{
                                    padding: '0.5rem 0.7rem',
                                    border: '1.5px solid #059669',
                                    borderRadius: '8px',
                                    background: 'rgba(5, 150, 105, 0.08)',
                                    color: '#059669',
                                    fontSize: '0.7rem', fontWeight: 700,
                                    cursor: 'pointer', whiteSpace: 'nowrap'
                                }}
                            >
                                Tudo
                            </button>
                        </div>

                        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                            <button
                                type="button"
                                onClick={() => setDevolverPrompt(null)}
                                style={{
                                    padding: '0.5rem 0.9rem',
                                    border: '1.5px solid #e5e7eb',
                                    borderRadius: '8px', background: '#fff',
                                    color: '#6b7280', fontSize: '0.75rem',
                                    fontWeight: 600, cursor: 'pointer'
                                }}
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                onClick={confirmarDevolucao}
                                style={{
                                    padding: '0.5rem 0.9rem',
                                    border: 'none', borderRadius: '8px',
                                    background: 'linear-gradient(135deg, #059669, #047857)',
                                    color: '#fff', fontSize: '0.75rem',
                                    fontWeight: 700, cursor: 'pointer'
                                }}
                            >
                                Devolver
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

// =============================================
// SEARCH INPUT
// =============================================
const SEARCH_DROPDOWN_OVERRIDE = `
#search-dropdown-floating {
    width: 500px !important;
    max-width: 500px !important;
    min-width: 0 !important;
    box-sizing: border-box !important;
    display: block !important;
    overflow-x: hidden !important;
}
#search-dropdown-floating .search-dropdown-item {
    display: flex !important;
    justify-content: space-between !important;
    align-items: center !important;
    gap: 0.5rem !important;
    width: 100% !important;
    max-width: 100% !important;
    min-width: 0 !important;
    box-sizing: border-box !important;
    overflow: hidden !important;
}
#search-dropdown-floating .search-dropdown-item-info {
    display: flex !important;
    flex-direction: column !important;
    flex: 1 1 auto !important;
    min-width: 0 !important;
    overflow: hidden !important;
}
#search-dropdown-floating .search-dropdown-item-info .item-name,
#search-dropdown-floating .search-dropdown-item-info .item-sub {
    display: block !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    white-space: nowrap !important;
    max-width: 100% !important;
}
#search-dropdown-floating .search-dropdown-item-actions {
    flex: 0 0 auto !important;
    display: flex !important;
    align-items: center !important;
    gap: 0.4rem !important;
    white-space: nowrap !important;
}
#search-dropdown-floating .search-dropdown-pagination {
    width: 100% !important;
    max-width: 100% !important;
    box-sizing: border-box !important;
}
`;

function SearchInput({
    value,
    onChange,
    onSelect,
    onClear,
    placeholder,
    items = [],
    getItemLabel,
    getItemSub,
    getItemTag,
    getItemPrice,
    getItemStatus,
    selectedId,
    iconName = 'search',
    noResultsMessage = 'Nenhum resultado encontrado',
    className = '',
    itemsPerPage = SEARCH_ITEMS_PER_PAGE,
    ...props
}) {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState(value || '');
    const [currentPage, setCurrentPage] = useState(1);
    const [dropdownStyle, setDropdownStyle] = useState({});
    const inputRef = useRef(null);
    const wrapperRef = useRef(null);
    const dropdownRef = useRef(null);

    useEffect(() => {
        const id = 'search-dropdown-override-style';
        if (document.getElementById(id)) return;
        const style = document.createElement('style');
        style.id = id;
        style.innerHTML = SEARCH_DROPDOWN_OVERRIDE;
        document.head.appendChild(style);
    }, []);

    useEffect(() => {
        setSearchTerm(value || '');
    }, [value]);

    const filteredItems = useMemo(() => {
        if (!items || !Array.isArray(items) || items.length === 0) return [];
        if (!searchTerm || searchTerm.trim() === '') return items;

        const search = searchTerm.toLowerCase().trim();

        return items.filter(item => {
            if (!item) return false;
            const searchableFields = [];

            if (getItemLabel) searchableFields.push(getItemLabel(item).toLowerCase());
            else if (item.nome) searchableFields.push(item.nome.toLowerCase());

            if (item.numero) searchableFields.push(String(item.numero).toLowerCase());
            if (item.nif) searchableFields.push(item.nif.toLowerCase());
            if (item.nuit) searchableFields.push(item.nuit.toLowerCase());
            if (item.email) searchableFields.push(item.email.toLowerCase());

            if (item.clienteInfo?.nome) searchableFields.push(item.clienteInfo.nome.toLowerCase());
            if (item.clienteId?.nome) searchableFields.push(item.clienteId.nome.toLowerCase());

            if (getItemStatus) {
                const st = getItemStatus(item);
                if (st) searchableFields.push(String(st).toLowerCase());
            }

            return searchableFields.some(f => f && f.includes(search));
        });
    }, [items, searchTerm, getItemLabel, getItemStatus]);

    const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedItems = filteredItems.slice(startIndex, startIndex + itemsPerPage);

    useEffect(() => { setCurrentPage(1); }, [searchTerm]);

    const updateDropdownPosition = useCallback(() => {
        if (!inputRef.current) return;

        const rect = inputRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        const dropdownMaxHeight = 320;
        const gap = 4;

        const safeWidth = 500;
        const safeLeft = Math.max(8, Math.min(rect.left, window.innerWidth - safeWidth - 8));

        const shouldOpenUp = spaceBelow < dropdownMaxHeight + 20 && spaceAbove > spaceBelow;

        const baseStyle = {
            position: 'fixed',
            left: safeLeft,
            width: '500px',
            maxWidth: '500px',
            minWidth: 0,
            boxSizing: 'border-box',
            zIndex: 999999
        };

        if (shouldOpenUp) {
            setDropdownStyle({
                ...baseStyle,
                top: 'auto',
                bottom: window.innerHeight - rect.top + gap,
                maxHeight: Math.min(dropdownMaxHeight, spaceAbove - 20)
            });
        } else {
            setDropdownStyle({
                ...baseStyle,
                top: rect.bottom + gap,
                bottom: 'auto',
                maxHeight: Math.min(dropdownMaxHeight, spaceBelow - 20)
            });
        }
    }, []);

    useEffect(() => {
        if (!isOpen) return;

        updateDropdownPosition();

        const handleScrollOrResize = () => updateDropdownPosition();
        window.addEventListener('scroll', handleScrollOrResize, true);
        window.addEventListener('resize', handleScrollOrResize);

        return () => {
            window.removeEventListener('scroll', handleScrollOrResize, true);
            window.removeEventListener('resize', handleScrollOrResize);
        };
    }, [isOpen, updateDropdownPosition]);

    useEffect(() => {
        if (!isOpen || !dropdownRef.current) return;
        const el = dropdownRef.current;
        el.style.setProperty('width', '500px', 'important');
        el.style.setProperty('max-width', '500px', 'important');
        el.style.setProperty('min-width', '0', 'important');
        el.style.setProperty('box-sizing', 'border-box', 'important');
        el.style.setProperty('display', 'block', 'important');
        el.style.setProperty('overflow-x', 'hidden', 'important');
    }, [isOpen, dropdownStyle]);

    const handleSelect = (item) => {
        if (onSelect) onSelect(item);
        setIsOpen(false);
        if (inputRef.current) inputRef.current.blur();
    };

    const handleClear = () => {
        if (onClear) onClear();
        setSearchTerm('');
        setIsOpen(false);
        if (inputRef.current) inputRef.current.focus();
    };

    const handleInputChange = (e) => {
        const val = e.target.value;
        setSearchTerm(val);
        if (onChange) onChange(val);
        setIsOpen(true);
        if (val === '' && onClear) onClear();
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            const dropdownEl = document.getElementById('search-dropdown-floating');
            if (
                wrapperRef.current &&
                !wrapperRef.current.contains(event.target) &&
                (!dropdownEl || !dropdownEl.contains(event.target))
            ) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const hasValue = !!selectedId || searchTerm?.trim() !== '';
    const preventFormSubmit = (e) => { if (e) { e.preventDefault(); e.stopPropagation(); } };

    const renderPagination = () => {
        if (totalPages <= 1) return null;

        const pageNumbers = (() => {
            const pages = [];
            const total = totalPages;
            const atual = currentPage;

            if (total <= 5) {
                for (let i = 1; i <= total; i++) pages.push(i);
                return pages;
            }

            pages.push(1);

            let inicio = Math.max(2, atual - 1);
            let fim = Math.min(total - 1, atual + 1);

            if (atual <= 3) { inicio = 2; fim = Math.min(total - 1, 4); }
            if (atual >= total - 2) { inicio = Math.max(2, total - 3); fim = total - 1; }

            if (inicio > 2) pages.push('...');
            for (let i = inicio; i <= fim; i++) pages.push(i);
            if (fim < total - 1) pages.push('...');

            pages.push(total);
            return pages;
        })();

        return (
            <div
                className="search-dropdown-pagination"
                style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    gap: '0.15rem', padding: '0.4rem 0.5rem',
                    borderTop: '1px solid #e5e7eb',
                    background: 'rgba(0,0,0,0.02)', flexWrap: 'wrap',
                    width: '100%', maxWidth: '100%', boxSizing: 'border-box'
                }}
                onMouseDown={preventFormSubmit}
                onClick={preventFormSubmit}
            >
                <button type="button"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setCurrentPage(prev => Math.max(1, prev - 1)); }}
                    disabled={currentPage === 1}
                    style={{
                        width: '24px', height: '24px',
                        border: '1px solid #e5e7eb', borderRadius: '50%',
                        background: 'transparent', color: '#1a1a2e',
                        cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        opacity: currentPage === 1 ? 0.4 : 1,
                        pointerEvents: currentPage === 1 ? 'none' : 'auto',
                        padding: 0
                    }}>
                    <Icon name="chevron_left" size={12} />
                </button>

                {pageNumbers.map((p, index) => {
                    if (p === '...') {
                        return (
                            <span key={`dots-${index}`} style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                minWidth: '20px', height: '24px',
                                color: '#6b7280', fontSize: '0.7rem',
                                fontWeight: 600, userSelect: 'none'
                            }}>…</span>
                        );
                    }

                    const isActive = p === currentPage;

                    return (
                        <button key={`page-${p}`} type="button"
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setCurrentPage(p); }}
                            style={{
                                minWidth: '24px', height: '24px', padding: '0 0.15rem',
                                border: isActive ? '1px solid #4f46e5' : '1px solid transparent',
                                borderRadius: '50%',
                                background: isActive ? '#4f46e5' : 'transparent',
                                color: isActive ? '#ffffff' : '#1a1a2e',
                                cursor: 'pointer',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: '0.6rem',
                                fontWeight: isActive ? 700 : 500
                            }}>
                            {p}
                        </button>
                    );
                })}

                <button type="button"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setCurrentPage(prev => Math.min(totalPages, prev + 1)); }}
                    disabled={currentPage === totalPages}
                    style={{
                        width: '24px', height: '24px',
                        border: '1px solid #e5e7eb', borderRadius: '50%',
                        background: 'transparent', color: '#1a1a2e',
                        cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        opacity: currentPage === totalPages ? 0.4 : 1,
                        pointerEvents: currentPage === totalPages ? 'none' : 'auto',
                        padding: 0
                    }}>
                    <Icon name="chevron_right" size={12} />
                </button>

                <span style={{ fontSize: '0.55rem', color: '#9ca3af', marginLeft: '0.2rem' }}>
                    {filteredItems.length} itens
                </span>
            </div>
        );
    };

    // 🔥 Renderiza o dropdown num Portal para escapar do stacking context do modal
    const dropdownContent = isOpen ? createPortal(
        <div
            id="search-dropdown-floating"
            ref={dropdownRef}
            className="search-dropdown"
            style={{
                ...dropdownStyle,
                maxWidth: '500px',
                boxSizing: 'border-box',
                background: '#ffffff',
                border: '1.5px solid #e5e7eb',
                borderRadius: '10px',
                boxShadow: '0 12px 40px rgba(0, 0, 0, 0.18), 0 4px 12px rgba(0, 0, 0, 0.08)',
                overflowY: 'auto',
                overflowX: 'hidden'
            }}
        >
            {filteredItems.length === 0 ? (
                <div className="search-dropdown-empty">
                    {items && items.length > 0 ? noResultsMessage : 'Carregando...'}
                </div>
            ) : (
                <>
                    {paginatedItems.map((item, index) => (
                        <div
                            key={item._id || index}
                            className="search-dropdown-item"
                            onClick={() => handleSelect(item)}
                            style={{
                                display: 'flex', justifyContent: 'space-between',
                                alignItems: 'center', gap: '0.5rem',
                                width: '100%', maxWidth: '100%',
                                minWidth: 0, boxSizing: 'border-box',
                                overflow: 'hidden'
                            }}
                        >
                            <div
                                className="search-dropdown-item-info"
                                style={{
                                    display: 'flex', flexDirection: 'column',
                                    flex: '1 1 auto', minWidth: 0, overflow: 'hidden'
                                }}
                            >
                                <span className="item-name" style={{
                                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                                }}>
                                    {getItemLabel ? getItemLabel(item) : item.nome}
                                </span>
                                {getItemSub && getItemSub(item) && (
                                    <span className="item-sub" style={{
                                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                                    }}>
                                        {getItemSub(item)}
                                    </span>
                                )}
                            </div>
                            <div className="search-dropdown-item-actions" style={{
                                flex: '0 0 auto', display: 'flex',
                                alignItems: 'center', gap: '0.4rem', whiteSpace: 'nowrap'
                            }}>
                                {getItemStatus && getItemStatus(item) && (
                                    <span className="item-tag" style={{
                                        fontSize: '0.55rem', padding: '2px 8px',
                                        borderRadius: '100px', background: '#e5e7eb',
                                        color: '#6b7280', fontWeight: 600, whiteSpace: 'nowrap'
                                    }}>
                                        {getItemStatus(item)}
                                    </span>
                                )}
                                {getItemPrice && getItemPrice(item) && (
                                    <span className="item-price" style={{ whiteSpace: 'nowrap' }}>
                                        {getItemPrice(item)}
                                    </span>
                                )}
                            </div>
                        </div>
                    ))}
                    {renderPagination()}
                </>
            )}
        </div>,
        document.body
    ) : null;

    return (
        <div className={`search-input-wrapper ${className}`} ref={wrapperRef}>
            <div className="search-input-inner">
                <Icon name={iconName} size={16} className={`search-icon ${hasValue ? 'has-value' : ''}`} />
                <input
                    ref={inputRef}
                    type="text"
                    className={`search-input-field ${hasValue ? 'has-value' : ''}`}
                    placeholder={placeholder}
                    value={searchTerm}
                    onChange={handleInputChange}
                    onFocus={() => setIsOpen(true)}
                    autoComplete="off"
                    {...props}
                />
                {selectedId && (
                    <button type="button" className="search-clear-btn" onClick={handleClear} title="Limpar seleção">
                        <Icon name="close" size={14} />
                    </button>
                )}
            </div>
            {dropdownContent}
        </div>
    );
}

// =============================================
// COMPONENTE PRINCIPAL
// =============================================
export default function GuiasEntrega() {
    const [guias, setGuias] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tableLoading, setTableLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filtroStatus, setFiltroStatus] = useState('todos');
    const [expandedRows, setExpandedRows] = useState({});
    const [itensPage, setItensPage] = useState({});

    // =============================================
    // 🔥 CORRIGIDO: states individuais de loading por ação
    // =============================================
    const [loadingPDF, setLoadingPDF] = useState(false);
    const [loadingPrint, setLoadingPrint] = useState(false);
    const [loadingApagarComps, setLoadingApagarComps] = useState(false);
    const [loadingComplementar, setLoadingComplementar] = useState(false);
    const [loadingEntregar, setLoadingEntregar] = useState(false);

    const [toast, setToast] = useState({ message: '', type: '' });
    const showToast = (text, type = 'success') => setToast({ message: text, type });
    const closeToast = () => setToast({ message: '', type: '' });

    const [empresaConfig, setEmpresaConfig] = useState({
        nome: '', nuit: '', email: '', telefone: '', website: '', cafe: '',
        imagemPerfil: null, morada: {},
        moeda: { simbolo: 'MT', codigo: 'MZN', casasDecimais: 2, separadorDecimal: ',', separadorMilhar: '.' },
        formatacao: { data: { formato: 'DD/MM/YYYY' } }
    });

    const [pagination, setPagination] = useState({ page: 1, limit: ITEMS_PER_PAGE, total: 0, pages: 0 });
    const [stats, setStats] = useState({ total: 0, emitidas: 0, entregues: 0, anuladas: 0 });

    // MODAIS
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showAnularModal, setShowAnularModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showEntregarModal, setShowEntregarModal] = useState(false);
    const [showComplementarModal, setShowComplementarModal] = useState(false);
    const [showApagarComplementaresModal, setShowApagarComplementaresModal] = useState(false);

    const [guiaDetalhes, setGuiaDetalhes] = useState(null);
    const [guiaEditando, setGuiaEditando] = useState(null);
    const [guiaToAnular, setGuiaToAnular] = useState(null);
    const [guiaToDelete, setGuiaToDelete] = useState(null);
    const [guiaToEntregar, setGuiaToEntregar] = useState(null);
    const [guiaToComplementar, setGuiaToComplementar] = useState(null);
    const [guiaToApagarComplementares, setGuiaToApagarComplementares] = useState(null);
    const [complementarIdParaApagar, setComplementarIdParaApagar] = useState(null);
    const [motivoAnulacao, setMotivoAnulacao] = useState('');
    const [recebidoPor, setRecebidoPor] = useState('');

    const [faturasDisponiveis, setFaturasDisponiveis] = useState([]);
    const [faturaSelecionada, setFaturaSelecionada] = useState(null);
    const [searchFatura, setSearchFatura] = useState('');
    const [itensGuia, setItensGuia] = useState([]);
    const [formData, setFormData] = useState({
        tipoEntrega: 'Total', transportadora: '', matricula: '', motorista: '',
        documentoMotorista: '', enderecoEntrega: '', cidadeEntrega: '', provinciaEntrega: '',
        contactoEntrega: '', observacoes: '',
        dataEmissao: new Date().toISOString().split('T')[0]
    });

    const token = localStorage.getItem('token');
    const api = useMemo(() => axios.create({

       /*============================
        Configuracao do API
         ============================*/                 

        baseURL: API_BASE_URL,
        headers: { Authorization: `Bearer ${token}` }
    }), [token]);



    // FORMATAÇÃO
    const formatarMoeda = useMemo(() => (valor) => {
        const cfg = empresaConfig.moeda;
        const sepDec = cfg.separadorDecimal || ',';
        const sepMil = cfg.separadorMilhar || '.';
        const casasDec = cfg.casasDecimais || 2;
        const simbolo = cfg.simbolo || 'MT';

        if (valor === undefined || valor === null || isNaN(valor)) {
            return `0${sepDec}${'0'.repeat(casasDec)} ${simbolo}`;
        }
        let v = Number(valor).toFixed(casasDec).replace('.', sepDec);
        const p = v.split(sepDec);
        p[0] = p[0].replace(/\B(?=(\d{3})+(?!\d))/g, sepMil);
        return `${p.join(sepDec)} ${simbolo}`;
    }, [empresaConfig.moeda]);

    const formatarData = useCallback((data) => {
        if (!data) return '-';
        const d = new Date(data);
        if (isNaN(d.getTime())) return '-';
        const cfg = empresaConfig.formatacao?.data || { formato: 'DD/MM/YYYY' };
        let f = cfg.formato || 'DD/MM/YYYY';
        return f
            .replace('DD', String(d.getDate()).padStart(2, '0'))
            .replace('MM', String(d.getMonth() + 1).padStart(2, '0'))
            .replace('YYYY', d.getFullYear());
    }, [empresaConfig.formatacao]);

    // CARREGAR
    const fetchGuias = useCallback(async (page = 1) => {
        setTableLoading(true);
        try {
            const params = new URLSearchParams();
            if (searchTerm) params.append('search', searchTerm);
            if (filtroStatus !== 'todos') params.append('status', filtroStatus);
            params.append('page', page);
            params.append('limit', ITEMS_PER_PAGE);

            const res = await api.get(`/guias-entrega?${params.toString()}`);
            if (res.data?.success) {
                setGuias(res.data.data || []);
                setPagination(res.data.pagination || { page, limit: ITEMS_PER_PAGE, total: 0, pages: 0 });

                if (res.data.empresa) {
                    const e = res.data.empresa;
                    setEmpresaConfig(prev => ({
                        ...prev,
                        nome: e.nome || prev.nome,
                        nuit: e.nuit || prev.nuit,
                        email: e.email || prev.email,
                        telefone: e.telefone || prev.telefone,
                        website: e.website || prev.website,
                        cafe: e.cafe || prev.cafe,
                        imagemPerfil: e.imagemPerfil || prev.imagemPerfil,
                        morada: e.morada || prev.morada,
                        moeda: e.moeda || prev.moeda,
                        formatacao: e.formatacao || prev.formatacao
                    }));
                }

                if (res.data.resumo) {
                    const s = { total: res.data.pagination?.total || 0, emitidas: 0, entregues: 0, anuladas: 0 };
                    res.data.resumo.forEach(r => {
                        if (r.status === 'Emitida') s.emitidas = r.count;
                        if (r.status === 'Entregue') s.entregues = r.count;
                        if (r.status === 'Anulada') s.anuladas = r.count;
                    });
                    setStats(s);
                }
            }
        } catch (e) {
            console.error('❌ Erro ao carregar guias:', e);
            showToast('Erro ao carregar guias', 'danger');
        } finally {
            setTableLoading(false);
            setLoading(false);
        }
    }, [searchTerm, filtroStatus, api]);

    // 🔥 Buscar faturas que ainda NÃO têm guia ativa
    const fetchFaturas = async () => {
        try {
            const res = await api.get('/faturas?limit=500');
            if (!res.data?.data) return;

            const faturasCandidatas = res.data.data.filter(x =>
                ['Emitida', 'Aberta', 'Pago', 'Vencida'].includes(x.status)
            );

            const resGuias = await api.get('/guias-entrega?limit=2000');
            const guiasAtivas = (resGuias.data?.data || []).filter(g =>
                g.status !== 'Anulada'
            );

            const faturasComGuia = new Set(
                guiasAtivas
                    .map(g => String(g.faturaId?._id || g.faturaId || ''))
                    .filter(Boolean)
            );

            const faturasSemGuia = faturasCandidatas.filter(f =>
                !faturasComGuia.has(String(f._id))
            );

            console.log(`📄 Faturas: ${faturasCandidatas.length} candidatas → ${faturasSemGuia.length} sem guia ativa`);

            setFaturasDisponiveis(faturasSemGuia);
        } catch (e) {
            console.error('❌ Erro ao carregar faturas:', e);
        }
    };

    useEffect(() => { fetchGuias(1); }, []); // eslint-disable-line

    // EXPANSÃO
    const toggleRow = (id) => {
        setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));
        if (!expandedRows[id]) {
            setItensPage(prev => ({ ...prev, [id]: 1 }));
        }
    };

    const changeItensPage = (id, page) => {
        setItensPage(prev => ({ ...prev, [id]: page }));
    };

    // CRIAR
    const abrirCriar = async () => {
        await fetchFaturas();
        setFaturaSelecionada(null);
        setSearchFatura('');
        setItensGuia([]);
        setFormData({
            tipoEntrega: 'Total', transportadora: '', matricula: '', motorista: '',
            documentoMotorista: '', enderecoEntrega: '', cidadeEntrega: '', provinciaEntrega: '',
            contactoEntrega: '', observacoes: '',
            dataEmissao: new Date().toISOString().split('T')[0]
        });
        setShowCreateModal(true);
    };

    const selecionarFatura = (fatura) => {
        if (!fatura) return;
        setFaturaSelecionada(fatura);
        setSearchFatura(fatura.numero + ' — ' + (fatura.clienteInfo?.nome || fatura.clienteId?.nome || ''));

        const itens = (fatura.produtos || []).map(p => ({
            produtoId: p.produtoId?._id || p.produtoId || null,
            servicoId: p.servicoId?._id || p.servicoId || null,
            nomeItem: p.nomeItem || p.produtoInfo?.nome || p.servicoId?.nome || 'Item',
            codigoItem: p.codigoItem || p.produtoInfo?.codigo || '',
            descricao: p.descricao || p.produtoInfo?.descricao || '',
            unidadeMedida: p.unidadeMedida || 'UN',
            quantidade: p.quantidade || 0
        }));
        setItensGuia(itens);

        const cli = fatura.clienteInfo || fatura.clienteId || fatura.faturaSnapshot?.cliente || {};
        const m = cli.morada || {};

        const partesEndereco = [
            cli.endereco,
            m.rua,
            m.numero,
            m.bairro,
            m.quarteirao ? `Q:${m.quarteirao}` : null,
            m.cidade || cli.cidade,
            m.provincia || cli.provincia,
            m.pais
        ].filter(v => v && String(v).trim());

        const enderecoCompleto = partesEndereco.join(', ').trim();

        setFormData(prev => ({
            ...prev,
            enderecoEntrega: enderecoCompleto || '',
            cidadeEntrega: m.cidade || cli.cidade || '',
            provinciaEntrega: m.provincia || cli.provincia || '',
            contactoEntrega: cli.telefone || ''
        }));
    };

    const limparSelecaoFatura = () => {
        setFaturaSelecionada(null);
        setSearchFatura('');
        setItensGuia([]);
    };

    const handleCriar = async (e) => {
        e.preventDefault();
        if (!faturaSelecionada) { showToast('Selecione uma fatura', 'danger'); return; }
        if (!itensGuia.length) { showToast('Adicione pelo menos um item', 'danger'); return; }

        try {
            setLoading(true);
            const payload = {
                faturaId: faturaSelecionada._id,
                itens: itensGuia.map(it => ({
                    produtoId: it.produtoId || null,
                    servicoId: it.servicoId || null,
                    nomeItem: it.nomeItem,
                    codigoItem: it.codigoItem,
                    descricao: it.descricao || '',
                    unidadeMedida: it.unidadeMedida || 'UN',
                    quantidade: parseFloat(it.quantidade) || 0
                })),
                tipoEntrega: formData.tipoEntrega,
                transportadora: formData.transportadora,
                matricula: formData.matricula,
                motorista: formData.motorista,
                documentoMotorista: formData.documentoMotorista,
                enderecoEntrega: formData.enderecoEntrega,
                cidadeEntrega: formData.cidadeEntrega,
                provinciaEntrega: formData.provinciaEntrega,
                contactoEntrega: formData.contactoEntrega,
                observacoes: formData.observacoes,
                dataEmissao: formData.dataEmissao
            };

            const res = await api.post('/guias-entrega', payload);
            if (res.data?.success) {
                showToast(`Guia ${res.data.data.numero} criada com sucesso!`, 'success');
                setShowCreateModal(false);
                fetchGuias(1);
            }
        } catch (e) {
            console.error('❌', e);
            showToast(e.response?.data?.erro || 'Erro ao criar guia', 'danger');
        } finally {
            setLoading(false);
        }
    };

    // EDITAR
    const abrirEditar = (guia) => {
        if (guia.bloqueadaParaEdicao) {
            showToast('Esta guia tem complementar associada. Apague a complementar primeiro.', 'warning');
            return;
        }

        const tipo = guia.tipoEntrega || 'Total';
        console.log('🔍 abrirEditar — tipoEntrega:', tipo, '| itensRemovidos:', guia.itensRemovidos?.length || 0);

        setGuiaEditando({
            ...guia,
            tipoEntrega: tipo,
            itens: (guia.itens || []).map(it => ({ ...it })),
            itensOriginais: (guia.itens || []).map(it => ({ ...it })),
            itensRemovidos: (guia.itensRemovidos || []).map(it => ({ ...it })),
            tipoEntregaOriginal: tipo
        });
        setShowEditModal(true);
    };

    const encontrarOriginalDoItem = (guia, item) => {
        if (!guia || !item) return null;
        const listaOriginais = guia.itensOriginais || [];
        return listaOriginais.find(o =>
            String(o.produtoId || '') === String(item.produtoId || '') &&
            o.nomeItem === item.nomeItem
        ) || null;
    };

    const devolverItemRemovido = (index, quantidade) => {
        if (!guiaEditando) return;
        const removidos = [...(guiaEditando.itensRemovidos || [])];
        const itemRemovido = removidos[index];
        if (!itemRemovido) return;

        const qDev = Math.max(0, Math.min(
            quantidade,
            parseFloat(itemRemovido.quantidade) || 0
        ));
        if (qDev <= 0) return;

        const restante = (parseFloat(itemRemovido.quantidade) || 0) - qDev;

        let novosRemovidos;
        if (restante > 0.001) {
            novosRemovidos = removidos.map((it, i) =>
                i === index ? { ...it, quantidade: restante } : it
            );
        } else {
            novosRemovidos = removidos.filter((_, i) => i !== index);
        }

        const itensAtivos = [...(guiaEditando.itens || [])];
        const idxAtivo = itensAtivos.findIndex(x =>
            String(x.produtoId) === String(itemRemovido.produtoId) &&
            x.nomeItem === itemRemovido.nomeItem
        );

        if (idxAtivo >= 0) {
            itensAtivos[idxAtivo] = {
                ...itensAtivos[idxAtivo],
                quantidade: (parseFloat(itensAtivos[idxAtivo].quantidade) || 0) + qDev
            };
        } else {
            itensAtivos.push({ ...itemRemovido, quantidade: qDev });
        }

        setGuiaEditando({
            ...guiaEditando,
            itens: itensAtivos,
            itensRemovidos: novosRemovidos
        });

        showToast(`Devolvido: ${qDev} × ${itemRemovido.nomeItem}`, 'success');
    };

    const handleEditar = async (e) => {
        e.preventDefault();
        if (!guiaEditando) return;
        if (!guiaEditando.itens?.length) {
            showToast('A guia deve ter pelo menos um item', 'danger');
            return;
        }

        try {
            setLoading(true);
            const payload = {
                itens: guiaEditando.itens.map(it => ({
                    produtoId: it.produtoId || null,
                    servicoId: it.servicoId || null,
                    nomeItem: it.nomeItem,
                    codigoItem: it.codigoItem,
                    descricao: it.descricao || '',
                    unidadeMedida: it.unidadeMedida || 'UN',
                    quantidade: parseFloat(it.quantidade) || 0
                })).filter(it => it.quantidade > 0),
                itensRemovidos: (guiaEditando.itensRemovidos || []).map(it => ({
                    produtoId: it.produtoId || null,
                    servicoId: it.servicoId || null,
                    nomeItem: it.nomeItem,
                    codigoItem: it.codigoItem,
                    descricao: it.descricao || '',
                    unidadeMedida: it.unidadeMedida || 'UN',
                    quantidade: parseFloat(it.quantidade) || 0
                })).filter(it => it.quantidade > 0),
                tipoEntrega: guiaEditando.tipoEntrega,
                transportadora: guiaEditando.transportadora,
                matricula: guiaEditando.matricula,
                motorista: guiaEditando.motorista,
                documentoMotorista: guiaEditando.documentoMotorista,
                enderecoEntrega: guiaEditando.enderecoEntrega,
                cidadeEntrega: guiaEditando.cidadeEntrega,
                provinciaEntrega: guiaEditando.provinciaEntrega,
                contactoEntrega: guiaEditando.contactoEntrega,
                observacoes: guiaEditando.observacoes,
                dataEmissao: guiaEditando.dataEmissao
            };

            const res = await api.put(`/guias-entrega/${guiaEditando._id}`, payload);
            if (res.data?.success) {
                const temRemovidos = (guiaEditando.itensRemovidos || []).length > 0;
                showToast(
                    temRemovidos
                        ? `Guia atualizada com ${guiaEditando.itensRemovidos.length} item(ns) removido(s) — aguardando guia complementar`
                        : 'Guia atualizada com sucesso!',
                    temRemovidos ? 'warning' : 'success'
                );
                setShowEditModal(false);
                setGuiaEditando(null);
                fetchGuias(pagination.page);
            }
        } catch (e) {
            showToast(e.response?.data?.erro || 'Erro ao editar guia', 'danger');
        } finally {
            setLoading(false);
        }
    };

    // AÇÕES
    const verDetalhes = async (guia) => {
        try {
            setLoading(true);
            const res = await api.get(`/guias-entrega/${guia._id}`);
            if (res.data?.success) {
                const dadosGuia = res.data.data;

                dadosGuia.tipoEntrega = dadosGuia.tipoEntrega || 'Total';
                dadosGuia.itens = dadosGuia.itens || [];
                dadosGuia.itensRemovidos = dadosGuia.itensRemovidos || [];
                dadosGuia.guiasComplementaresIds = dadosGuia.guiasComplementaresIds || [];
                dadosGuia.bloqueadaParaEdicao = dadosGuia.bloqueadaParaEdicao || false;

                if (!dadosGuia.clienteInfo) {
                    dadosGuia.clienteInfo = dadosGuia.faturaSnapshot?.cliente || {};
                }
                if (!dadosGuia.clienteInfo.endereco && dadosGuia.clienteId?.morada) {
                    const m = dadosGuia.clienteId.morada;
                    const partes = [
                        dadosGuia.clienteId.endereco,
                        m.rua, m.numero, m.bairro,
                        m.cidade, m.provincia, m.pais
                    ].filter(Boolean);
                    dadosGuia.clienteInfo.endereco = partes.join(', ');
                }
                if (!dadosGuia.clienteInfo.nuit && dadosGuia.clienteId) {
                    dadosGuia.clienteInfo.nuit = dadosGuia.clienteId.nuit || dadosGuia.clienteId.nif || '';
                }

                setGuiaDetalhes(dadosGuia);

                if (res.data.empresa) {
                    const e = res.data.empresa;
                    setEmpresaConfig(prev => ({
                        ...prev,
                        nome: e.nome || prev.nome, nuit: e.nuit || prev.nuit,
                        email: e.email || prev.email, telefone: e.telefone || prev.telefone,
                        website: e.website || prev.website, cafe: e.cafe || prev.cafe,
                        imagemPerfil: e.imagemPerfil || prev.imagemPerfil,
                        morada: e.morada || prev.morada, moeda: e.moeda || prev.moeda,
                        formatacao: e.formatacao || prev.formatacao
                    }));
                }
                setShowDetailsModal(true);
            }
        } catch (e) {
            showToast('Erro ao carregar detalhes', 'danger');
        } finally {
            setLoading(false);
        }
    };

    const rastrearPar = async (guia, tipo, complementarIdEspecifico = null) => {
        try {
            setLoading(true);

            let alvoId = null;

            if (tipo === 'complementar') {
                if (complementarIdEspecifico) {
                    alvoId = complementarIdEspecifico?._id || complementarIdEspecifico;
                } else {
                    const comps = guia.guiasComplementaresIds || [];
                    alvoId = comps[0]?._id || comps[0] || null;
                }
            } else {
                alvoId = guia.guiaOrigemId?._id || guia.guiaOrigemId;
            }

            if (!alvoId) {
                showToast(
                    tipo === 'complementar'
                        ? 'Esta guia ainda não tem complementar gerada'
                        : 'Esta guia não tem origem registada',
                    'warning'
                );
                return;
            }

            setShowDetailsModal(false);
            setGuiaDetalhes(null);

            const res = await api.get(`/guias-entrega/${alvoId}`);
            if (res.data?.success) {
                const dadosGuia = res.data.data;

                dadosGuia.tipoEntrega = dadosGuia.tipoEntrega || 'Total';
                dadosGuia.itens = dadosGuia.itens || [];
                dadosGuia.itensRemovidos = dadosGuia.itensRemovidos || [];
                dadosGuia.guiasComplementaresIds = dadosGuia.guiasComplementaresIds || [];
                dadosGuia.bloqueadaParaEdicao = dadosGuia.bloqueadaParaEdicao || false;

                if (!dadosGuia.clienteInfo) {
                    dadosGuia.clienteInfo = dadosGuia.faturaSnapshot?.cliente || {};
                }
                if (!dadosGuia.clienteInfo.endereco && dadosGuia.clienteId?.morada) {
                    const m = dadosGuia.clienteId.morada;
                    const partes = [
                        dadosGuia.clienteId.endereco,
                        m.rua, m.numero, m.bairro,
                        m.cidade, m.provincia, m.pais
                    ].filter(Boolean);
                    dadosGuia.clienteInfo.endereco = partes.join(', ');
                }
                if (!dadosGuia.clienteInfo.nuit && dadosGuia.clienteId) {
                    dadosGuia.clienteInfo.nuit = dadosGuia.clienteId.nuit || dadosGuia.clienteId.nif || '';
                }

                setTimeout(() => {
                    setGuiaDetalhes(dadosGuia);
                    setShowDetailsModal(true);
                    showToast(
                        tipo === 'complementar'
                            ? `A abrir complementar ${dadosGuia.numero}`
                            : `A abrir origem ${dadosGuia.numero}`,
                        'info'
                    );
                }, 200);
            }
        } catch (e) {
            console.error('❌ Erro ao rastrear guia:', e);
            showToast('Erro ao rastrear guia', 'danger');
        } finally {
            setLoading(false);
        }
    };

    const abrirEntregar = (guia) => {
        setGuiaToEntregar(guia);
        setRecebidoPor(guia.recebidoPor || '');
        setShowEntregarModal(true);
    };

    const confirmarEntregar = async () => {
        if (!guiaToEntregar) return;
        try {
            setLoadingEntregar(true); // 🔥 CORRIGIDO
            const res = await api.post(`/guias-entrega/${guiaToEntregar._id}/entregar`, {
                recebidoPor, dataEntrega: new Date()
            });
            if (res.data?.success) {
                const sugestao = res.data?.sugestaoComplementar;
                if (sugestao?.podeGerarComplementar) {
                    showToast(
                        `Guia entregue! Existem ${sugestao.totalItensRemovidos} itens removidos — considere gerar uma guia complementar.`,
                        'warning'
                    );
                } else {
                    showToast('Guia marcada como entregue!', 'success');
                }
                setShowEntregarModal(false);
                setShowDetailsModal(false);
                setGuiaToEntregar(null);
                setRecebidoPor('');
                fetchGuias(pagination.page);
            }
        } catch (e) {
            showToast(e.response?.data?.erro || 'Erro ao marcar como entregue', 'danger');
        } finally {
            setLoadingEntregar(false); // 🔥 CORRIGIDO
        }
    };

    const abrirComplementar = async (guia) => {
        if (guia?.itensRemovidos?.length) {
            setGuiaToComplementar(guia);
            setShowComplementarModal(true);
            return;
        }

        try {
            setLoading(true);
            const res = await api.get(`/guias-entrega/${guia._id}`);
            if (res.data?.success) {
                const dadosGuia = res.data.data;
                if (!dadosGuia.itensRemovidos?.length) {
                    showToast('Não há itens removidos para gerar complementar', 'warning');
                    return;
                }
                setGuiaToComplementar(dadosGuia);
                setShowComplementarModal(true);
            }
        } catch (e) {
            showToast('Erro ao carregar detalhes da guia', 'danger');
        } finally {
            setLoading(false);
        }
    };

    const confirmarComplementar = async () => {
        if (!guiaToComplementar) return;
        try {
            setLoadingComplementar(true); // 🔥 CORRIGIDO
            const res = await api.post(`/guias-entrega/${guiaToComplementar._id}/complementar`);
            if (res.data?.success) {
                const novaQtd = res.data?.complementaresAtuais || 1;
                const maxComp = res.data?.maxComplementares || MAX_COMPLEMENTARES;
                showToast(
                    `Guia complementar ${res.data.data.numero} criada! (${novaQtd}/${maxComp})`,
                    'success'
                );
                setShowComplementarModal(false);
                setShowDetailsModal(false);
                setGuiaToComplementar(null);
                setTimeout(() => {
                    fetchGuias(pagination.page);
                }, 300);
            }
        } catch (e) {
            showToast(e.response?.data?.erro || 'Erro ao gerar complementar', 'danger');
        } finally {
            setLoadingComplementar(false); // 🔥 CORRIGIDO
        }
    };

    // 🔥 NOVO — Abrir modal de apagar complementares
    const abrirApagarComplementares = (guia, complementarId = null) => {
        setGuiaToApagarComplementares(guia);
        setComplementarIdParaApagar(complementarId);
        setShowApagarComplementaresModal(true);
    };

    // 🔥 NOVO — Confirmar apagar complementares
    const confirmarApagarComplementares = async () => {
        if (!guiaToApagarComplementares) return;
        try {
            setLoadingApagarComps(true); // 🔥 CORRIGIDO

            const url = complementarIdParaApagar
                ? `/api/guias-entrega/${guiaToApagarComplementares._id}/complementar/${complementarIdParaApagar}`
                : `/api/guias-entrega/${guiaToApagarComplementares._id}/complementar`;

            const res = await api.delete(url);

            if (res.data?.success) {
                showToast(
                    `${res.data.message}. ${res.data.itensDevolvidos || 0} itens devolvidos à origem.`,
                    'success'
                );

                setShowApagarComplementaresModal(false);
                setShowDetailsModal(false);
                setGuiaToApagarComplementares(null);
                setComplementarIdParaApagar(null);
                setGuiaDetalhes(null);

                setTimeout(() => {
                    fetchGuias(pagination.page);
                }, 300);
            }
        } catch (e) {
            showToast(e.response?.data?.erro || 'Erro ao apagar complementar', 'danger');
        } finally {
            setLoadingApagarComps(false); // 🔥 CORRIGIDO
        }
    };

    const anularGuia = async () => {
        if (!motivoAnulacao.trim()) { showToast('Informe o motivo', 'warning'); return; }
        try {
            setLoading(true);
            const res = await api.post(`/guias-entrega/${guiaToAnular._id}/anular`, { motivo: motivoAnulacao });
            if (res.data?.success) {
                showToast('Guia anulada!', 'success');
                setShowAnularModal(false);
                setShowDetailsModal(false);
                setGuiaToAnular(null);
                setMotivoAnulacao('');
                fetchGuias(pagination.page);
            }
        } catch (e) {
            showToast(e.response?.data?.erro || 'Erro ao anular', 'danger');
        } finally {
            setLoading(false);
        }
    };

    const excluirGuia = async () => {
        try {
            setLoading(true);
            const res = await api.delete(`/guias-entrega/${guiaToDelete._id}`);
            if (res.data?.success) {
                showToast('Guia excluída!', 'success');
                setShowDeleteModal(false);
                setGuiaToDelete(null);
                fetchGuias(pagination.page);
            }
        } catch (e) {
            showToast(e.response?.data?.erro || 'Erro ao excluir', 'danger');
        } finally {
            setLoading(false);
        }
    };

    // 🔥 CORRIGIDO: usa loadingPDF
    const baixarPDF = async (guia) => {
        if (guia?.status === 'Anulada') {
            showToast('Esta guia foi anulada e não pode ser exportada.', 'warning');
            return;
        }

        try {
            setLoadingPDF(true);
            const res = await api.get(`/guias-entrega/${guia._id}/pdf`, { responseType: 'blob' });
            const blob = new Blob([res.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Guia_${guia.numero}.pdf`;
            document.body.appendChild(a);
            a.click();
            setTimeout(() => { document.body.removeChild(a); window.URL.revokeObjectURL(url); }, 150);
            showToast('PDF baixado!', 'success');
        } catch (e) {
            showToast('Erro ao gerar PDF', 'danger');
        } finally {
            setLoadingPDF(false);
        }
    };

    // 🔥 CORRIGIDO: usa loadingPrint
    const imprimirGuia = async (guia) => {
        if (guia?.status === 'Anulada') {
            showToast('Esta guia foi anulada e não pode ser impressa.', 'warning');
            return;
        }

        try {
            setLoadingPrint(true);
            const res = await api.get(`/guias-entrega/${guia._id}/pdf`, { responseType: 'blob' });
            const blob = new Blob([res.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const printWindow = window.open(url, '_blank');
            if (!printWindow) {
                showToast('Permita pop-ups para imprimir', 'warning');
                window.URL.revokeObjectURL(url);
                return;
            }
            printWindow.onload = function () { setTimeout(() => printWindow.print(), 500); };
            showToast('Documento enviado para impressão', 'success');
        } catch (e) {
            showToast('Erro ao imprimir', 'danger');
        } finally {
            setLoadingPrint(false);
        }
    };

    // EVENTO EXTERNO (fatura → guia)
    useEffect(() => {
        const handleCriarGuia = (event) => {
            const fatura = event.detail?.fatura;
            if (fatura) {
                const abrirComFatura = async () => {
                    await fetchFaturas();
                    setShowCreateModal(true);
                    setTimeout(() => { setFaturaSelecionada(fatura); selecionarFatura(fatura); }, 100);
                };
                abrirComFatura();
            }
        };
        window.addEventListener('criar-guia-entrega', handleCriarGuia);
        return () => window.removeEventListener('criar-guia-entrega', handleCriarGuia);
    }, []);

    // PAGINAÇÃO PRINCIPAL
    const renderPagination = () => {
        const { page, pages, total } = pagination;
        if (pages <= 1) return null;
        const startItem = (page - 1) * pagination.limit + 1;
        const endItem = Math.min(page * pagination.limit, total);

        const getPageNumbers = (currentPage, totalPages) => {
            const arr = [];
            if (totalPages <= 5) { for (let i = 1; i <= totalPages; i++) arr.push(i); return arr; }
            arr.push(1);
            let s = Math.max(2, currentPage - 1), e = Math.min(totalPages - 1, currentPage + 1);
            if (currentPage <= 3) { s = 2; e = Math.min(totalPages - 1, 4); }
            if (currentPage >= totalPages - 2) { s = Math.max(2, totalPages - 3); e = totalPages - 1; }
            if (s > 2) arr.push('...');
            for (let i = s; i <= e; i++) arr.push(i);
            if (e < totalPages - 1) arr.push('...');
            arr.push(totalPages);
            return arr;
        };

        return (
            <div className="cotacoes-pagination">
                <div className="pagination-info">
                    <span>Mostrando {startItem} - {endItem} de {total} guias</span>
                </div>
                <div className="pagination-controls">
                    <button className="pagination-btn" onClick={() => fetchGuias(page - 1)} disabled={page === 1}>
                        <Icon name="chevron_left" size={18} />
                    </button>
                    <div className="pagination-numbers">
                        {getPageNumbers(page, pages).map((p, i) => p === '...'
                            ? <span key={`dots-${i}`} className="pagination-dots">…</span>
                            : <button key={p} className={`pagination-number ${page === p ? 'active' : ''}`}
                                onClick={() => fetchGuias(p)}>{p}</button>
                        )}
                    </div>
                    <button className="pagination-btn" onClick={() => fetchGuias(page + 1)} disabled={page === pages}>
                        <Icon name="chevron_right" size={18} />
                    </button>
                </div>
            </div>
        );
    };

    // =============================================
    // RENDER
    // =============================================
    return (
        <div className="cotacoes-container">
            {/* HEADER */}
            <div className="cotacoes-header">
                <div className="header-title">
                    <h2><Icon name="local_shipping" size={24} /> Guias de Entrega</h2>
                    <p>Emissão e gestão de guias de entrega</p>
                </div>
                <div className="header-actions">
                    <button className="btn-refresh" onClick={() => fetchGuias(pagination.page)} title="Atualizar">
                        <Icon name="refresh" size={18} />
                    </button>
                    <button className="btn-create" onClick={abrirCriar}>
                        <Icon name="add" size={18} /> Nova Guia
                    </button>
                </div>
            </div>

            {/* KPIs */}
            <div className="cotacoes-kpi-grid">
                <div className="kpi-card primary">
                    <div className="kpi-icon"><Icon name="local_shipping" /></div>
                    <div className="kpi-info">
                        <span className="kpi-value">{stats.total}</span>
                        <span className="kpi-label">Total Guias</span>
                    </div>
                </div>
                <div className="kpi-card info">
                    <div className="kpi-icon"><Icon name="description" /></div>
                    <div className="kpi-info">
                        <span className="kpi-value">{stats.emitidas}</span>
                        <span className="kpi-label">Emitidas</span>
                    </div>
                </div>
                <div className="kpi-card success">
                    <div className="kpi-icon"><Icon name="delivery_truck" /></div>
                    <div className="kpi-info">
                        <span className="kpi-value">{stats.entregues}</span>
                        <span className="kpi-label">Entregues</span>
                    </div>
                </div>
                <div className="kpi-card warning">
                    <div className="kpi-icon"><Icon name="cancel" /></div>
                    <div className="kpi-info">
                        <span className="kpi-value">{stats.anuladas}</span>
                        <span className="kpi-label">Anuladas</span>
                    </div>
                </div>
            </div>

            {/* FILTROS */}
            <div className="cotacoes-filters">
                <div className="search-box">
                    <Icon name="search" size={18} />
                    <input
                        type="text"
                        placeholder="Pesquisar por nº, cliente, fatura..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && fetchGuias(1)}
                    />
                    {searchTerm && (
                        <button className="search-clear" onClick={() => { setSearchTerm(''); fetchGuias(1); }}>
                            <Icon name="close" size={16} />
                        </button>
                    )}
                </div>
                <div className="filter-actions">
                    <select className="filter-select" value={filtroStatus}
                        onChange={(e) => { setFiltroStatus(e.target.value); setTimeout(() => fetchGuias(1), 0); }}>
                        <option value="todos">Todos os Status</option>
                        {STATUS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                    <button className="btn-filter-apply" onClick={() => fetchGuias(1)}>
                        <Icon name="search" size={16} /> Aplicar
                    </button>
                    <button className="btn-filter-clear" onClick={() => {
                        setSearchTerm(''); setFiltroStatus('todos');
                        setTimeout(() => fetchGuias(1), 100);
                    }}>
                        <Icon name="clear" size={16} />
                    </button>
                </div>
            </div>

            {/* TABELA */}
            <div className="cotacoes-table-container">
                {tableLoading && (
                    <div className="table-loading-overlay active">
                        <div className="spinner"></div>
                        <span className="loading-text">A carregar guias...</span>
                    </div>
                )}
                <div className="cotacoes-table-wrapper">
                    <table className="cotacoes-table">
                        <thead>
                            <tr>
                                <th className="col-expand" style={{ width: 40, textAlign: 'center' }}></th>
                                <th className="col-numero">Nº</th>
                                <th className="col-cliente">Cliente</th>
                                <th className="col-fatura" style={{ textAlign: 'center' }}>Fatura</th>
                                <th style={{ textAlign: 'center' }}>Emissão</th>
                                <th style={{ textAlign: 'center' }}>Entrega</th>
                                <th className="col-status" style={{ textAlign: 'center' }}>Status</th>
                                <th className="col-acoes" style={{ textAlign: 'center', width: 220 }}>Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && !tableLoading ? (
                                Array.from({ length: 5 }).map((_, i) => (
                                    <tr key={i} className="skeleton-row">
                                        <td><div className="skeleton-line" style={{ width: '20%' }}></div></td>
                                        <td><div className="skeleton-line" style={{ width: '40%' }}></div></td>
                                        <td><div className="skeleton-line" style={{ width: '60%' }}></div></td>
                                        <td><div className="skeleton-line" style={{ width: '30%' }}></div></td>
                                        <td><div className="skeleton-line" style={{ width: '30%' }}></div></td>
                                        <td><div className="skeleton-line" style={{ width: '30%' }}></div></td>
                                        <td><div className="skeleton-badge"></div></td>
                                        <td><div className="skeleton-actions">
                                            <div className="skeleton-circle small"></div>
                                            <div className="skeleton-circle small"></div>
                                        </div></td>
                                    </tr>
                                ))
                            ) : guias.length === 0 ? (
                                <tr className="empty-row">
                                    <td colSpan="8">
                                        <div className="empty-state">
                                            <Icon name="local_shipping" size={48} />
                                            <p>Nenhuma guia de entrega encontrada</p>
                                            <button className="btn-create-small" onClick={abrirCriar}>
                                                <Icon name="add" size={16} /> Criar primeira guia
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ) : guias.map(g => {
                                const st = getStatusInfo(g.status);
                                const isExpanded = expandedRows[g._id];

                                const clienteNome = getClienteNome(g);
                                const clienteNuit = getClienteNuit(g);

                                const faturaNum = g.faturaSnapshot?.numero || g.faturaId?.numero || '—';
                                const totalItens = g.itens?.length || 0;

                                const complementaresArray = g.guiasComplementaresIds || [];
                                const qtdComplementares = complementaresArray.length;
                                const temRemovidosPendentes =
                                    (g.itensRemovidos?.length || 0) > 0 && qtdComplementares < MAX_COMPLEMENTARES;
                                const isComplementar = !!g.guiaOrigemId;
                                const gerouComplementar = qtdComplementares > 0;
                                const atingiuLimiteComplementares = qtdComplementares >= MAX_COMPLEMENTARES;
                                const podeGerarComplementar = !atingiuLimiteComplementares;
                                const numOrigem = g.guiaOrigemId?.numero || null;
                                const bloqueadaParaEdicao = g.bloqueadaParaEdicao || false;

                                const currentItensPage = itensPage[g._id] || 1;
                                const totalItensPages = Math.ceil(totalItens / ITEMS_PER_PAGE);
                                const startIndex = (currentItensPage - 1) * ITEMS_PER_PAGE;
                                const endIndex = Math.min(startIndex + ITEMS_PER_PAGE, totalItens);
                                const itensPaginados = g.itens?.slice(startIndex, endIndex) || [];

                                const rowClass =
                                    g.status === 'Anulada' ? 'cancelled-row' :
                                        g.status === 'Entregue' ? 'approved-row' :
                                            temRemovidosPendentes ? 'pendente-complementar-row' :
                                                isComplementar ? 'guia-complementar-row' :
                                                    gerouComplementar ? 'gerou-complementar-row' :
                                                        '';

                                const podeEditar = g.status === 'Emitida' && !bloqueadaParaEdicao;

                                return (
                                    <React.Fragment key={g._id}>
                                        <tr className={`cotacao-row ${rowClass} ${isExpanded ? 'expanded' : ''}`}>
                                            <td className="col-expand" style={{ textAlign: 'center' }}>
                                                {totalItens > 0 && (
                                                    <button className="btn-expand-row" onClick={() => toggleRow(g._id)}
                                                        title={isExpanded ? 'Recolher itens' : 'Expandir itens'}
                                                        style={{
                                                            background: 'none', border: 'none', cursor: 'pointer',
                                                            color: '#6b7280', transition: 'transform 0.3s ease',
                                                            transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                                                            padding: '4px', display: 'flex',
                                                            alignItems: 'center', justifyContent: 'center'
                                                        }}>
                                                        <Icon name="chevron_right" size={18} />
                                                    </button>
                                                )}
                                                {totalItens === 0 && (
                                                    <span style={{ color: '#d1d5db', fontSize: '12px' }}>•</span>
                                                )}
                                            </td>
                                            <td className="col-numero">
                                                <span className="numero-badge">{g.numero}</span>

                                                {temRemovidosPendentes && !gerouComplementar && (
                                                    <span
                                                        className="badge-guia-complementar origem"
                                                        title={`${g.itensRemovidos.length} item(ns) removido(s) — aguardando complementar`}
                                                        style={{ display: 'block' }}
                                                    >
                                                        Pendente
                                                    </span>
                                                )}
                                                {isComplementar && (
                                                    <span
                                                        className="badge-guia-complementar complementar"
                                                        title={`Esta guia é complementar de ${numOrigem || '?'}`}
                                                        style={{ display: 'block' }}
                                                    >
                                                        Complementar
                                                    </span>
                                                )}
                                                {gerouComplementar && (
                                                    <span
                                                        className="badge-guia-complementar gerou"
                                                        title={`Esta guia gerou ${qtdComplementares} complementar(es)`}
                                                        style={{ display: 'block' }}
                                                    >
                                                        ✓ {qtdComplementares}/{MAX_COMPLEMENTARES}
                                                    </span>
                                                )}
                                                {bloqueadaParaEdicao && !isComplementar && (
                                                    <span
                                                        className="badge-guia-complementar"
                                                        title="Bloqueada para edição"
                                                        style={{
                                                            display: 'block',
                                                            background: 'rgba(107, 114, 128, 0.15)',
                                                            color: '#6b7280',
                                                            border: '1px solid rgba(107, 114, 128, 0.3)'
                                                        }}
                                                    >
                                                        Bloqueada
                                                    </span>
                                                )}
                                            </td>
                                            <td className="col-cliente">
                                                <div className="cliente-info">
                                                    <strong>{clienteNome}</strong>
                                                    {clienteNuit && (
                                                        <span className="nif">NUIT: {clienteNuit}</span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="col-fatura" style={{ textAlign: 'center' }}>
                                                <span className="numero-badge" style={{ fontSize: '0.5rem' }}>{faturaNum}</span>
                                            </td>
                                            <td style={{ textAlign: 'center' }}>{formatarData(g.dataEmissao)}</td>
                                            <td style={{ textAlign: 'center' }}>{g.dataEntrega ? formatarData(g.dataEntrega) : '—'}</td>
                                            <td className="col-status" style={{ textAlign: 'center' }}>
                                                <span className={`status-badge ${(g.status || '').toLowerCase()}`}>
                                                    <Icon name={g.statusIcon || st.icon} size={12} />
                                                    {g.statusFormatado || st.label}
                                                </span>
                                            </td>
                                            <td className="col-acoes" style={{ textAlign: 'center' }}>
                                                <div className="actions-cell">

                                                    {gerouComplementar && complementaresArray.map((comp, idx) => {
                                                        const compId = comp._id || comp;
                                                        const compNum = comp.numero || '?';
                                                        return (
                                                            <button
                                                                key={`comp-${idx}`}
                                                                className="action-btn"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    rastrearPar(g, 'complementar', compId);
                                                                }}
                                                                title={`Ver complementar ${idx + 1}/${qtdComplementares}: ${compNum}`}
                                                                style={{
                                                                    background: 'none',
                                                                    color: '#059669',
                                                                    border: 'none',
                                                                    position: 'relative'
                                                                }}
                                                            >
                                                                <Icon name="description" size={16} />
                                                                {qtdComplementares > 1 && (
                                                                    <span style={{
                                                                        position: 'absolute',
                                                                        top: '-4px',
                                                                        right: '-4px',
                                                                        background: '#059669',
                                                                        color: '#fff',
                                                                        borderRadius: '50%',
                                                                        width: '12px',
                                                                        height: '12px',
                                                                        fontSize: '0.5rem',
                                                                        fontWeight: 800,
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        lineHeight: 1
                                                                    }}>
                                                                        {idx + 1}
                                                                    </span>
                                                                )}
                                                            </button>
                                                        );
                                                    })}

                                                    {isComplementar && (
                                                        <button
                                                            className="action-btn"
                                                            onClick={(e) => { e.stopPropagation(); rastrearPar(g, 'origem'); }}
                                                            title={`Ver guia de origem: ${numOrigem || '?'}`}
                                                            style={{
                                                                background: 'none',
                                                                color: '#8b5cf6',
                                                                border: 'none'
                                                            }}
                                                        >
                                                            <Icon name="description" size={16} />
                                                        </button>
                                                    )}

                                                    <button className="action-btn view" onClick={() => verDetalhes(g)} title="Ver detalhes">
                                                        <Icon name="visibility" size={16} />
                                                    </button>

                                                    {g.status === 'Emitida' && !bloqueadaParaEdicao && (
                                                        <button className="action-btn edit" onClick={() => abrirEditar(g)} title="Editar Guia">
                                                            <Icon name="edit" size={16} />
                                                        </button>
                                                    )}
                                                    {g.status === 'Emitida' && bloqueadaParaEdicao && (
                                                        <button
                                                            className="action-btn"
                                                            onClick={() => showToast('Esta guia tem complementar associada. Apague a complementar primeiro.', 'warning')}
                                                            title="Bloqueada para edição"
                                                            style={{
                                                                background: 'none',
                                                                color: '#6b7280',
                                                                border: 'none',
                                                                cursor: 'not-allowed'
                                                            }}
                                                        >
                                                            <Icon name="lock" size={16} />
                                                        </button>
                                                    )}

                                                    {temRemovidosPendentes && g.status !== 'Anulada' && g.status !== 'Entregue' && (
                                                        <button
                                                            className="action-btn"
                                                            onClick={() => abrirComplementar(g)}
                                                            title={`Gerar guia complementar ${qtdComplementares + 1}/${MAX_COMPLEMENTARES} (${g.itensRemovidos.length} itens)`}
                                                            style={{
                                                                background: 'none',
                                                                color: '#d97706',
                                                                border: 'none'
                                                            }}
                                                        >
                                                            <Icon name="add_box" size={16} />
                                                        </button>
                                                    )}

                                                    {gerouComplementar && g.status !== 'Entregue' && g.status !== 'Anulada' && (
                                                        <button
                                                            className="action-btn"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                abrirApagarComplementares(g);
                                                            }}
                                                            title={`Apagar todas as ${qtdComplementares} complementares`}
                                                            style={{
                                                                background: 'none',
                                                                color: '#dc2626',
                                                                border: 'none'
                                                            }}
                                                        >
                                                            <Icon name="delete_sweep" size={16} />
                                                        </button>
                                                    )}

                                                    {g.status === 'Emitida' && (() => {
                                                        const bloquear = temRemovidosPendentes;
                                                        return (
                                                            <button
                                                                className="action-btn finalize"
                                                                onClick={() => {
                                                                    if (bloquear) {
                                                                        showToast(`Gere a complementar ${qtdComplementares + 1}/${MAX_COMPLEMENTARES} antes de entregar`, 'warning');
                                                                        return;
                                                                    }
                                                                    abrirEntregar(g);
                                                                }}
                                                                disabled={bloquear}
                                                                title={bloquear
                                                                    ? `${g.itensRemovidos.length} item(ns) removido(s) — gere a complementar primeiro`
                                                                    : 'Marcar Entregue'}
                                                                style={{
                                                                    opacity: bloquear ? 0.35 : 1,
                                                                    cursor: bloquear ? 'not-allowed' : 'pointer'
                                                                }}
                                                            >
                                                                <Icon name={bloquear ? 'lock' : 'delivery_truck'} size={16} />
                                                            </button>
                                                        );
                                                    })()}

                                                    {g.status !== 'Anulada' && g.status !== 'Entregue' && !gerouComplementar && (
                                                        <button className="action-btn cancel" onClick={() => {
                                                            setGuiaToAnular(g); setMotivoAnulacao(''); setShowAnularModal(true);
                                                        }} title="Anular">
                                                            <Icon name="cancel" size={16} />
                                                        </button>
                                                    )}
                                                    {g.status !== 'Anulada' && g.status !== 'Entregue' && gerouComplementar && (
                                                        <button
                                                            className="action-btn"
                                                            onClick={() => showToast('Apague primeiro as complementares antes de anular.', 'warning')}
                                                            title="Anular bloqueado"
                                                            style={{
                                                                background: 'none',
                                                                color: '#6b7280',
                                                                border: 'none',
                                                                cursor: 'not-allowed'
                                                            }}
                                                        >
                                                            <Icon name="lock" size={16} />
                                                        </button>
                                                    )}

                                                    {g.status === 'Anulada' && (
                                                        <button className="action-btn delete" onClick={() => {
                                                            setGuiaToDelete(g); setShowDeleteModal(true);
                                                        }} title="Excluir">
                                                            <Icon name="delete" size={16} />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                        {isExpanded && totalItens > 0 && (
                                            <tr className="expanded-details-row">
                                                <td colSpan="8" style={{ padding: 0 }}>
                                                    <div className="expanded-itens-container">
                                                        <div className="expanded-itens-header">
                                                            <span className="expanded-itens-title">
                                                                <Icon name="list" size={14} /> Itens da Guia
                                                            </span>
                                                            <span className="expanded-itens-count">
                                                                Total: {totalItens} {totalItens === 1 ? 'item' : 'itens'}
                                                                {totalItensPages > 1 && ` | Pág. ${currentItensPage} de ${totalItensPages}`}
                                                            </span>
                                                        </div>
                                                        <table className="itens-expanded-table">
                                                            <thead>
                                                                <tr>
                                                                    <th className="col-item">Item</th>
                                                                    <th className="col-tipo">Tipo</th>
                                                                    <th className="col-qtd">Qtd</th>
                                                                    <th className="col-unidade-header">Unidade</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {itensPaginados.map((item, idx) => (
                                                                    <tr key={idx} className="expanded-item-row">
                                                                        <td className="col-item">
                                                                            <span className="item-nome">{item.nomeItem}</span>
                                                                            {item.codigoItem && (
                                                                                <span className="item-codigo">({item.codigoItem})</span>
                                                                            )}
                                                                            {item.descricao && (
                                                                                <div style={{
                                                                                    fontSize: '0.55rem',
                                                                                    color: '#6b7280',
                                                                                    marginTop: '2px',
                                                                                    fontStyle: 'italic'
                                                                                }}>
                                                                                    {item.descricao}
                                                                                </div>
                                                                            )}
                                                                        </td>
                                                                        <td className="col-tipo">
                                                                            <span className={`tipo-badge ${item.servicoId ? 'servico' : 'produto'}`}>
                                                                                {item.servicoId ? 'Serviço' : 'Produto'}
                                                                            </span>
                                                                        </td>
                                                                        <td className="col-qtd">{item.quantidade}</td>
                                                                        <td className="col-unidade">{item.unidadeMedida || 'UN'}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                        {totalItensPages > 1 && (
                                                            <ItensPagination
                                                                currentPage={currentItensPage}
                                                                totalPages={totalItensPages}
                                                                onPageChange={(page) => changeItensPage(g._id, page)}
                                                            />
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {renderPagination()}

            {/* MODAL CRIAR */}
            {showCreateModal && (
                <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
                    <div className="modal-content modal-large" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3><Icon name="add_box" size={22} /> Nova Guia de Entrega</h3>
                            <button className="modal-close" onClick={() => setShowCreateModal(false)}>×</button>
                        </div>
                        <form onSubmit={handleCriar} className="modal-body-pdf-criar">
                            <div className="form-section">
                                <h4>1. Fatura de Origem</h4>
                                <div className="form-row">
                                    <div className="form-group" style={{ flex: '1 1 100%' }}>
                                        <label>Fatura <span className="required">*</span></label>
                                        <SearchInput
                                            value={searchFatura}
                                            onChange={(val) => {
                                                setSearchFatura(val);
                                                if (val === '') limparSelecaoFatura();
                                            }}
                                            onSelect={selecionarFatura}
                                            onClear={limparSelecaoFatura}
                                            placeholder="Buscar fatura por número ou cliente..."
                                            items={faturasDisponiveis}
                                            getItemLabel={(f) => `Fatura ${f.numero}`}
                                            getItemSub={(f) => f.clienteInfo?.nome || f.clienteId?.nome || 'Cliente'}
                                            getItemTag={(f) => f.status || 'Aberta'}
                                            getItemPrice={(f) => f.totalFormatado || formatarMoeda(f.total)}
                                            selectedId={faturaSelecionada?._id}
                                            iconName="receipt"
                                            iconName="hidden"
                                            itemsPerPage={SEARCH_ITEMS_PER_PAGE}
                                            noResultsMessage="Nenhuma fatura sem guia ativa encontrada"
                                        />
                                        <div style={{
                                            fontSize: '0.65rem',
                                            color: '#9ca3af',
                                            marginTop: '4px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '4px'
                                        }}>
                                            <Icon name="info" size={12} />
                                            <span>Apenas faturas sem guia ativa aparecem nesta lista.</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {faturaSelecionada && (
                                <>
                                    <div className="form-section">
                                        <h4>2. Itens a entregar</h4>
                                        <div className="itens-list">
                                            <table className="itens-table">
                                                <thead>
                                                    <tr>
                                                        <th style={{ width: '70%' }}>Item</th>
                                                        <th style={{ width: '30%', textAlign: 'center' }}>Qtd</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {itensGuia.map((it, i) => {
                                                        const maxQtd = Number(faturaSelecionada.itens?.[i]?.quantidade) || 0;
                                                        const isTotal = formData.tipoEntrega === 'Total';
                                                        return (
                                                            <tr key={i}>
                                                                <td>
                                                                    <div className="produto-info">
                                                                        <span className="produto-nome">{it.nomeItem}</span>
                                                                        {it.codigoItem && (
                                                                            <span className="produto-codigo">{it.codigoItem}</span>
                                                                        )}
                                                                    </div>
                                                                </td>
                                                                <td style={{ textAlign: 'center' }}>
                                                                    <input
                                                                        type="number"
                                                                        min="0"
                                                                        max={isTotal ? undefined : maxQtd}
                                                                        value={it.quantidade}
                                                                        disabled={isTotal}
                                                                        onChange={(e) => {
                                                                            const q = parseFloat(e.target.value) || 0;
                                                                            const valorFinal = isTotal ? q : Math.min(q, maxQtd);
                                                                            const novos = [...itensGuia];
                                                                            novos[i].quantidade = valorFinal;
                                                                            setItensGuia(novos);
                                                                        }}
                                                                        style={{
                                                                            width: 90,
                                                                            textAlign: 'center',
                                                                            background: isTotal ? '#f3f4f6' : '#fff',
                                                                            color: isTotal ? '#6b7280' : '#1a1a2e',
                                                                            cursor: isTotal ? 'not-allowed' : 'text'
                                                                        }}
                                                                        title={isTotal ? 'Entrega total — não editável' : `Máximo: ${maxQtd}`}
                                                                    />
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>

                                    <div className="form-section">
                                        <h4>3. Dados de Transporte</h4>
                                        <div className="form-row">
                                            <div className="form-group">
                                                <label>Tipo de Entrega</label>
                                                <select
                                                    value={formData.tipoEntrega}
                                                    onChange={e => {
                                                        const novoTipo = e.target.value;
                                                        setFormData({ ...formData, tipoEntrega: novoTipo });

                                                        if (novoTipo === 'Total' && faturaSelecionada?.itens) {
                                                            const restaurados = itensGuia.map((it, i) => ({
                                                                ...it,
                                                                quantidade: Number(faturaSelecionada.itens[i]?.quantidade) || it.quantidade
                                                            }));
                                                            setItensGuia(restaurados);
                                                        }
                                                    }}
                                                >
                                                    <option value="Total">Total</option>

                                                </select>
                                            </div>
                                            <div className="form-group">
                                                <label>Transportadora</label>
                                                <input type="text" value={formData.transportadora}
                                                    onChange={e => setFormData({ ...formData, transportadora: e.target.value })}
                                                    placeholder="Nome da transportadora" />
                                            </div>
                                        </div>
                                        <div className="form-row">
                                            <div className="form-group">
                                                <label>Motorista</label>
                                                <input type="text" value={formData.motorista}
                                                    onChange={e => setFormData({ ...formData, motorista: e.target.value })} />
                                            </div>
                                            <div className="form-group">
                                                <label>Matrícula</label>
                                                <input type="text" value={formData.matricula}
                                                    onChange={e => setFormData({ ...formData, matricula: e.target.value })} />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="form-section">
                                        <h4>4. Local de Entrega</h4>
                                        <div className="form-row">
                                            <div className="form-group" style={{ flex: '1 1 100%' }}>
                                                <label>Endereço</label>
                                                <input type="text" value={formData.enderecoEntrega}
                                                    onChange={e => setFormData({ ...formData, enderecoEntrega: e.target.value })} />
                                            </div>
                                        </div>
                                        <div className="form-row">
                                            <div className="form-group">
                                                <label>Cidade</label>
                                                <input type="text" value={formData.cidadeEntrega}
                                                    onChange={e => setFormData({ ...formData, cidadeEntrega: e.target.value })} />
                                            </div>
                                            <div className="form-group">
                                                <label>Província</label>
                                                <input type="text" value={formData.provinciaEntrega}
                                                    onChange={e => setFormData({ ...formData, provinciaEntrega: e.target.value })} />
                                            </div>
                                            <div className="form-group">
                                                <label>Contacto</label>
                                                <input type="text" value={formData.contactoEntrega}
                                                    onChange={e => setFormData({ ...formData, contactoEntrega: e.target.value })} />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="form-section">
                                        <h4>5. Observações</h4>
                                        <textarea value={formData.observacoes}
                                            onChange={e => setFormData({ ...formData, observacoes: e.target.value })}
                                            rows="2" placeholder="Observações adicionais..." />
                                    </div>
                                </>
                            )}

                            <div className="modal-actions">
                                <button type="submit" className="modal-btn-primary"
                                    disabled={loading || !faturaSelecionada}>
                                    {loading ? 'A criar...' : 'Criar Guia'}
                                </button>
                                <button type="button" className="modal-btn-secondary"
                                    onClick={() => setShowCreateModal(false)}>Cancelar</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* MODAL EDITAR */}
            {showEditModal && guiaEditando && (() => {
                const tipoAtual = guiaEditando.tipoEntrega || 'Total';
                const isParcial = tipoAtual === 'Parcial';
                const isTotal = !isParcial;

                return (
                    <div className="modal-overlay" onClick={() => { setShowEditModal(false); setGuiaEditando(null); }}>
                        <div className="modal-content modal-large" onClick={e => e.stopPropagation()}>
                            <div className="modal-header">
                                <h3><Icon name="edit" size={22} /> Editar Guia {guiaEditando.numero}</h3>
                                <button className="modal-close" onClick={() => { setShowEditModal(false); setGuiaEditando(null); }}>×</button>
                            </div>
                            <form onSubmit={handleEditar} className="modal-body-pdf-criar">

                                <div className="form-section">
                                    <h4>1. Itens a entregar</h4>
                                    <div className="itens-list">
                                        <table className="itens-table">
                                            <thead>
                                                <tr>
                                                    <th style={{ width: isParcial ? '55%' : '80%' }}>Item</th>
                                                    <th style={{ width: '30%', textAlign: 'center' }}>Qtd</th>
                                                    {isParcial && (
                                                        <th style={{ width: '15%', textAlign: 'center' }}>Ações</th>
                                                    )}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {guiaEditando.itens.map((it, i) => {
                                                    const original = encontrarOriginalDoItem(guiaEditando, it);
                                                    const maxQtd = Number(original?.quantidade)
                                                        || Number(it.quantidade)
                                                        || 0;

                                                    return (
                                                        <tr key={i}>
                                                            <td>
                                                                <div className="produto-info">
                                                                    <span className="produto-nome">{it.nomeItem}</span>
                                                                    {it.codigoItem && (
                                                                        <span className="produto-codigo">{it.codigoItem}</span>
                                                                    )}
                                                                </div>
                                                            </td>
                                                            <td style={{ textAlign: 'center' }}>
                                                                <div style={{
                                                                    display: 'inline-flex',
                                                                    alignItems: 'center',
                                                                    gap: '4px',
                                                                    border: '1.5px solid #e5e7eb',
                                                                    borderRadius: '8px',
                                                                    padding: '2px',
                                                                    background: isTotal ? '#f3f4f6' : '#fff'
                                                                }}>
                                                                    {isParcial && (
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => {
                                                                                const novoValor = Math.max(0, (parseFloat(it.quantidade) || 0) - 1);
                                                                                const novos = [...guiaEditando.itens];
                                                                                novos[i].quantidade = novoValor;
                                                                                setGuiaEditando({ ...guiaEditando, itens: novos });
                                                                            }}
                                                                            disabled={(parseFloat(it.quantidade) || 0) <= 0}
                                                                            title="Reduzir 1"
                                                                            style={{
                                                                                width: '24px', height: '24px',
                                                                                border: 'none', borderRadius: '6px',
                                                                                background: 'rgba(239, 68, 68, 0.08)',
                                                                                color: '#ef4444',
                                                                                cursor: (parseFloat(it.quantidade) || 0) <= 0 ? 'not-allowed' : 'pointer',
                                                                                opacity: (parseFloat(it.quantidade) || 0) <= 0 ? 0.4 : 1,
                                                                                fontSize: '0.85rem', fontWeight: 800,
                                                                                display: 'flex', alignItems: 'center',
                                                                                justifyContent: 'center', padding: 0
                                                                            }}
                                                                        >
                                                                            −
                                                                        </button>
                                                                    )}

                                                                    <input
                                                                        type="number"
                                                                        min="0"
                                                                        max={isParcial ? maxQtd : undefined}
                                                                        value={it.quantidade}
                                                                        disabled={isTotal}
                                                                        onChange={(e) => {
                                                                            const q = parseFloat(e.target.value) || 0;
                                                                            const valorFinal = isParcial
                                                                                ? Math.min(q, maxQtd)
                                                                                : q;
                                                                            const novos = [...guiaEditando.itens];
                                                                            novos[i].quantidade = valorFinal;
                                                                            setGuiaEditando({ ...guiaEditando, itens: novos });
                                                                        }}
                                                                        style={{
                                                                            width: '55px',
                                                                            textAlign: 'center',
                                                                            background: 'transparent',
                                                                            border: 'none',
                                                                            color: isTotal ? '#6b7280' : '#1a1a2e',
                                                                            cursor: isTotal ? 'not-allowed' : 'text',
                                                                            fontSize: '0.8rem',
                                                                            fontWeight: 600,
                                                                            outline: 'none',
                                                                            padding: '2px 0'
                                                                        }}
                                                                        title={isTotal ? 'Entrega total — não editável' : `Máximo: ${maxQtd}`}
                                                                    />

                                                                    {isParcial && (
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => {
                                                                                const novoValor = Math.min(maxQtd, (parseFloat(it.quantidade) || 0) + 1);
                                                                                const novos = [...guiaEditando.itens];
                                                                                novos[i].quantidade = novoValor;
                                                                                setGuiaEditando({ ...guiaEditando, itens: novos });
                                                                            }}
                                                                            disabled={(parseFloat(it.quantidade) || 0) >= maxQtd}
                                                                            title="Aumentar 1"
                                                                            style={{
                                                                                width: '24px', height: '24px',
                                                                                border: 'none', borderRadius: '6px',
                                                                                background: 'rgba(5, 150, 105, 0.08)',
                                                                                color: '#059669',
                                                                                cursor: (parseFloat(it.quantidade) || 0) >= maxQtd ? 'not-allowed' : 'pointer',
                                                                                opacity: (parseFloat(it.quantidade) || 0) >= maxQtd ? 0.4 : 1,
                                                                                fontSize: '0.85rem', fontWeight: 800,
                                                                                display: 'flex', alignItems: 'center',
                                                                                justifyContent: 'center', padding: 0
                                                                            }}
                                                                        >
                                                                            +
                                                                        </button>
                                                                    )}
                                                                </div>
                                                                {isParcial && (
                                                                    <div style={{
                                                                        fontSize: '0.6rem',
                                                                        color: '#9ca3af',
                                                                        marginTop: '2px'
                                                                    }}>
                                                                        máx: {maxQtd}
                                                                    </div>
                                                                )}
                                                            </td>
                                                            {isParcial && (
                                                                <td style={{ textAlign: 'center' }}>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => {
                                                                            const itemRemovido = original || guiaEditando.itens[i];
                                                                            const novos = guiaEditando.itens.filter((_, idx) => idx !== i);
                                                                            const removidosAtuais = guiaEditando.itensRemovidos || [];
                                                                            setGuiaEditando({
                                                                                ...guiaEditando,
                                                                                itens: novos,
                                                                                itensRemovidos: [...removidosAtuais, { ...itemRemovido }]
                                                                            });
                                                                        }}
                                                                        title="Remover item (será criada guia complementar depois)"
                                                                        aria-label="Remover item"
                                                                        style={{
                                                                            background: 'rgba(239, 68, 68, 0.08)',
                                                                            border: '1px solid rgba(239, 68, 68, 0.25)',
                                                                            cursor: 'pointer',
                                                                            color: '#ef4444',
                                                                            padding: '5px',
                                                                            borderRadius: '6px',
                                                                            display: 'inline-flex',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'center',
                                                                            transition: 'all 0.15s ease'
                                                                        }}
                                                                        onMouseEnter={(e) => {
                                                                            e.currentTarget.style.background = 'rgba(239, 68, 68, 0.15)';
                                                                        }}
                                                                        onMouseLeave={(e) => {
                                                                            e.currentTarget.style.background = 'rgba(239, 68, 68, 0.08)';
                                                                        }}
                                                                    >
                                                                        <Icon name="delete" size={16} />
                                                                    </button>
                                                                </td>
                                                            )}
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>

                                    <RemovidosGrid
                                        itens={guiaEditando.itensRemovidos || []}
                                        title="Itens Removidos"
                                        onDevolver={devolverItemRemovido}
                                    />
                                </div>

                                <div className="form-section">
                                    <h4>2. Dados de Transporte</h4>
                                    <div className="form-row">
                                        <div className="form-group">
                                            <label>Tipo de Entrega</label>
                                            <select
                                                value={guiaEditando.tipoEntrega || 'Total'}
                                                onChange={e => {
                                                    const novoTipo = e.target.value;
                                                    const origem = guiaEditando.itensOriginais || [];

                                                    let novosItens = guiaEditando.itens;
                                                    let removidos = guiaEditando.itensRemovidos || [];

                                                    if (novoTipo === 'Total') {
                                                        novosItens = guiaEditando.itens.map((it) => {
                                                            const orig = origem.find(o =>
                                                                String(o.produtoId || '') === String(it.produtoId || '') &&
                                                                o.nomeItem === it.nomeItem
                                                            );
                                                            return {
                                                                ...it,
                                                                quantidade: Number(orig?.quantidade) || it.quantidade
                                                            };
                                                        });
                                                        const todosOriginais = [...novosItens];
                                                        for (const rem of removidos) {
                                                            const jaExiste = todosOriginais.some(x =>
                                                                String(x.produtoId || '') === String(rem.produtoId || '') &&
                                                                x.nomeItem === rem.nomeItem
                                                            );
                                                            if (!jaExiste) {
                                                                todosOriginais.push({ ...rem });
                                                            }
                                                        }
                                                        novosItens = todosOriginais;
                                                        removidos = [];
                                                    }

                                                    setGuiaEditando({
                                                        ...guiaEditando,
                                                        tipoEntrega: novoTipo,
                                                        itens: novosItens,
                                                        itensRemovidos: removidos
                                                    });
                                                }}
                                            >
                                                <option value="Total">Total</option>
                                                <option value="Parcial">Parcial</option>
                                            </select>
                                        </div>
                                        <div className="form-group">
                                            <label>Transportadora</label>
                                            <input
                                                type="text"
                                                value={guiaEditando.transportadora || ''}
                                                onChange={e => setGuiaEditando({ ...guiaEditando, transportadora: e.target.value })}
                                            />
                                        </div>
                                    </div>
                                    <div className="form-row">
                                        <div className="form-group">
                                            <label>Motorista</label>
                                            <input
                                                type="text"
                                                value={guiaEditando.motorista || ''}
                                                onChange={e => setGuiaEditando({ ...guiaEditando, motorista: e.target.value })}
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>Matrícula</label>
                                            <input
                                                type="text"
                                                value={guiaEditando.matricula || ''}
                                                onChange={e => setGuiaEditando({ ...guiaEditando, matricula: e.target.value })}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="form-section">
                                    <h4>3. Local de Entrega</h4>
                                    <div className="form-row">
                                        <div className="form-group" style={{ flex: '1 1 100%' }}>
                                            <label>Endereço</label>
                                            <input
                                                type="text"
                                                value={guiaEditando.enderecoEntrega || ''}
                                                onChange={e => setGuiaEditando({ ...guiaEditando, enderecoEntrega: e.target.value })}
                                            />
                                        </div>
                                    </div>
                                    <div className="form-row">
                                        <div className="form-group">
                                            <label>Cidade</label>
                                            <input
                                                type="text"
                                                value={guiaEditando.cidadeEntrega || ''}
                                                onChange={e => setGuiaEditando({ ...guiaEditando, cidadeEntrega: e.target.value })}
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>Província</label>
                                            <input
                                                type="text"
                                                value={guiaEditando.provinciaEntrega || ''}
                                                onChange={e => setGuiaEditando({ ...guiaEditando, provinciaEntrega: e.target.value })}
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>Contacto</label>
                                            <input
                                                type="text"
                                                value={guiaEditando.contactoEntrega || ''}
                                                onChange={e => setGuiaEditando({ ...guiaEditando, contactoEntrega: e.target.value })}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="form-section">
                                    <h4>4. Observações</h4>
                                    <textarea
                                        value={guiaEditando.observacoes || ''}
                                        onChange={e => setGuiaEditando({ ...guiaEditando, observacoes: e.target.value })}
                                        rows="2"
                                        placeholder="Observações adicionais..."
                                    />
                                </div>

                                <div className="modal-actions">
                                    <button type="submit" className="modal-btn-primary" disabled={loading}>
                                        {loading ? 'A guardar...' : 'Guardar Alterações'}
                                    </button>
                                    <button
                                        type="button"
                                        className="modal-btn-secondary"
                                        onClick={() => { setShowEditModal(false); setGuiaEditando(null); }}
                                    >
                                        Cancelar
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                );
            })()}

            {/* MODAL DETALHES */}
            {showDetailsModal && guiaDetalhes && (
                <div className="modal-overlay" onClick={() => setShowDetailsModal(false)}>
                    <div className="modal-container-dual" onClick={e => e.stopPropagation()}>
                        <div className="modal-content modal-pdf" style={{ position: 'relative', overflow: 'hidden' }}>
                            {guiaDetalhes.status === 'Anulada' && (
                                <div className="watermark-overlay" style={{
                                    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    pointerEvents: 'none', zIndex: 10
                                }}>
                                    <div className="watermark-text" style={{
                                        fontSize: '60px', fontWeight: 900,
                                        color: 'rgba(220, 38, 38, 0.12)',
                                        textTransform: 'uppercase', transform: 'rotate(-30deg)',
                                        letterSpacing: '15px', userSelect: 'none',
                                        fontFamily: 'Arial, Helvetica, sans-serif',
                                        border: '6px solid rgba(220, 38, 38, 0.15)',
                                        padding: '20px 50px', borderRadius: '12px',
                                        background: 'rgba(220, 38, 38, 0.03)'
                                    }}>ANULADA</div>
                                </div>
                            )}

                            <div className="modal-header">
                                <h3>
                                    <Icon name="local_shipping" size={22} />
                                    Guia {guiaDetalhes.numero}
                                    {guiaDetalhes.status === 'Anulada' && (
                                        <span style={{
                                            marginLeft: '10px', fontSize: '0.7rem', fontWeight: 700,
                                            color: '#dc2626', background: 'rgba(220, 38, 38, 0.1)',
                                            padding: '2px 12px', borderRadius: '100px', letterSpacing: '1px'
                                        }}>ANULADA</span>
                                    )}

                                    {guiaDetalhes.guiaOrigemId && (
                                        <button
                                            onClick={(e) => { e.stopPropagation(); rastrearPar(guiaDetalhes, 'origem'); }}
                                            title={`Ir para guia de origem: ${guiaDetalhes.guiaOrigemId?.numero || '?'}`}
                                            style={{
                                                marginLeft: '10px', fontSize: '0.65rem', fontWeight: 700,
                                                color: '#7c3aed', background: 'rgba(139, 92, 246, 0.15)',
                                                padding: '4px 12px', borderRadius: '100px', letterSpacing: '0.5px',
                                                border: '1px solid rgba(139, 92, 246, 0.4)',
                                                cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px'
                                            }}
                                        >
                                            <Icon name="description" size={12} />
                                            COMPLEMENTAR DE {guiaDetalhes.guiaOrigemId?.numero || '?'}
                                        </button>
                                    )}

                                    {(guiaDetalhes.guiasComplementaresIds?.length || 0) > 0 && (
                                        <div style={{
                                            display: 'inline-flex',
                                            gap: '4px',
                                            marginLeft: '10px',
                                            verticalAlign: 'middle'
                                        }}>
                                            {guiaDetalhes.guiasComplementaresIds.map((comp, idx) => {
                                                const compId = comp._id || comp;
                                                const compNum = comp.numero || '?';
                                                const total = guiaDetalhes.guiasComplementaresIds.length;
                                                return (
                                                    <button
                                                        key={`badge-comp-${idx}`}
                                                        onClick={(e) => { e.stopPropagation(); rastrearPar(guiaDetalhes, 'complementar', compId); }}
                                                        title={`Ir para guia complementar: ${compNum}`}
                                                        style={{
                                                            fontSize: '0.6rem', fontWeight: 700,
                                                            color: '#059669', background: 'rgba(16, 185, 129, 0.15)',
                                                            padding: '3px 10px', borderRadius: '100px', letterSpacing: '0.4px',
                                                            border: '1px solid rgba(16, 185, 129, 0.4)',
                                                            cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '3px'
                                                        }}
                                                    >
                                                        <Icon name="description" size={11} />
                                                        {total > 1 ? `${idx + 1}/${total}` : ''} {compNum}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}
                                </h3>
                                <button className="modal-close" onClick={() => setShowDetailsModal(false)}>×</button>
                            </div>

                            <div className="modal-body pdf-body">
                                <div className="pdf-header-top">
                                    <div className="pdf-header-left">
                                        <span className="pdf-label">Guia de Entrega</span>
                                        <span className="pdf-value">{guiaDetalhes.numero}</span>
                                    </div>
                                    <div className="pdf-header-right">
                                        <span className="pdf-label">Original</span>
                                        <span className="pdf-label">Data de Emissão</span>
                                        <span className="pdf-value">{formatarData(guiaDetalhes.dataEmissao)}</span>
                                    </div>
                                </div>

                                <div className="pdf-cliente-empresa-grid">
                                    <div className="pdf-empresa-section">
                                        <div className="pdf-empresa-grid">
                                            <div
                                                className="pdf-empresa-row-logo-empresas"
                                                style={{
                                                    display: 'flex',
                                                    justifyContent: 'flex-start',
                                                    alignItems: 'flex-start',
                                                    textAlign: 'left'
                                                }}
                                            >
                                                {empresaConfig.imagemPerfil ? (
                                                    <img
                                                        src={empresaConfig.imagemPerfil}
                                                        alt={empresaConfig.nome}
                                                        className="pdf-empresa-logo"
                                                        style={{
                                                            maxHeight: '70px',
                                                            maxWidth: '280px',
                                                            objectFit: 'contain',
                                                            display: 'block',
                                                            marginRight: 'auto',
                                                            marginLeft: '-70px'
                                                        }}
                                                        onError={(e) => { e.target.style.display = 'none'; }}
                                                    />
                                                ) : (
                                                    <span className="pdf-empresa-value-guiaentrega">
                                                        {empresaConfig.nome || '—'}
                                                    </span>
                                                )}
                                            </div>

                                            <div className="pdf-empresa-row">
                                                <span className="pdf-empresa-value-guiaentrega">
                                                    {empresaConfig.nome || '—'}
                                                </span>
                                            </div>

                                            <div className="pdf-empresa-row">
                                                <span className="pdf-empresa-value-small">
                                                    {empresaConfig.morada?.enderecoCompleto || '—'}
                                                </span>
                                            </div>

                                            <div className="pdf-empresa-row" style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                                                <span style={{ fontSize: '8px', fontWeight: 700, whiteSpace: 'nowrap' }}>NUIT :</span>
                                                <span style={{ fontSize: '8px', whiteSpace: 'nowrap' }}>{empresaConfig.nuit || '—'}</span>
                                            </div>

                                            <div className="pdf-empresa-row">
                                                <span className="pdf-empresa-value-small">{empresaConfig.email || '—'}</span>
                                            </div>

                                            <div className="pdf-empresa-row">
                                                <span className="pdf-empresa-value-small">
                                                    {(empresaConfig.website || '—').replace(/^https?:\/\//, '').replace(/\/$/, '')}
                                                </span>
                                            </div>

                                            <div className="pdf-empresa-row">
                                                <span className="pdf-empresa-value-small">{empresaConfig.telefone || '—'}</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="pdf-cliente-section">
                                        <div className="pdf-cliente-grid">
                                            <div className="pdf-cliente-row" style={{ marginTop: '50px' }}></div>

                                            <div className="pdf-cliente-row-nome">
                                                <span className="pdf-cliente-value bold">
                                                    {guiaDetalhes.clienteInfo?.nome
                                                        || guiaDetalhes.faturaSnapshot?.cliente?.nome
                                                        || '—'}
                                                </span>
                                            </div>

                                            <div className="pdf-cliente-row">
                                                <span className="pdf-cliente-value small">
                                                    {guiaDetalhes.clienteInfo?.endereco
                                                        || guiaDetalhes.faturaSnapshot?.cliente?.endereco
                                                        || '—'}
                                                </span>
                                            </div>

                                            <div className="pdf-cliente-row" style={{ display: 'flex', alignItems: 'baseline', gap: 4, flexWrap: 'nowrap', whiteSpace: 'nowrap' }}>
                                                <span style={{ fontSize: '8px', fontWeight: 700, whiteSpace: 'nowrap' }}>NUIT :</span>
                                                <span className="pdf-cliente-value small" style={{ whiteSpace: 'nowrap' }}>
                                                    {guiaDetalhes.clienteInfo?.nuit
                                                        || guiaDetalhes.clienteInfo?.nif
                                                        || guiaDetalhes.faturaSnapshot?.cliente?.nuit
                                                        || guiaDetalhes.faturaSnapshot?.cliente?.nif
                                                        || '—'}
                                                </span>
                                            </div>

                                            <div className="pdf-cliente-row">
                                                <span className="pdf-cliente-value small">
                                                    {guiaDetalhes.clienteInfo?.telefone
                                                        || guiaDetalhes.faturaSnapshot?.cliente?.telefone
                                                        || guiaDetalhes.contactoEntrega
                                                        || '—'}
                                                </span>
                                            </div>

                                            <div className="pdf-cliente-row">
                                                <span className="pdf-cliente-value small">
                                                    {guiaDetalhes.clienteInfo?.email
                                                        || guiaDetalhes.faturaSnapshot?.cliente?.email
                                                        || '—'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="pdf-processado">
                                    <span className="pdf-empresa-label">Processado por computador</span>
                                    {empresaConfig.cafe && <span className="pdf-cafe-code">CAFE: {empresaConfig.cafe}</span>}
                                </div>

                                <div className="pdf-info-row">
                                    <div className="pdf-info-item">
                                        <span className="pdf-info-label">Fatura Nº</span>
                                        <span className="pdf-info-value">{guiaDetalhes.faturaSnapshot?.numero || '—'}</span>
                                    </div>
                                    <div className="pdf-info-item">
                                        <span className="pdf-info-label">Tipo de Entrega</span>
                                        <span className="pdf-info-value">
                                            {guiaDetalhes.tipoEntregaLabel
                                                || (guiaDetalhes.tipoEntrega === 'Parcial' ? 'Entrega Parcial' : 'Entrega Total')}
                                        </span>
                                    </div>
                                    <div className="pdf-info-item">
                                        <span className="pdf-info-label">Itens / Quantidades</span>
                                        <span className="pdf-info-value">
                                            {guiaDetalhes.itens?.length || 0} itens ·{' '}
                                            {guiaDetalhes.itens?.reduce((s, i) => s + (parseFloat(i.quantidade) || 0), 0)} un.
                                        </span>
                                    </div>
                                </div>

                                <div className="pdf-info-row entrega-row">
                                    <div className="pdf-info-item" style={{ gridColumn: '1 / -1' }}>
                                        <span className="pdf-info-label">Endereço de Entrega</span>
                                        <span className="pdf-info-value" style={{ fontWeight: 600 }}>
                                            {guiaDetalhes.enderecoEntrega || '—'}
                                            {guiaDetalhes.cidadeEntrega && `, ${guiaDetalhes.cidadeEntrega}`}
                                            {guiaDetalhes.provinciaEntrega && `, ${guiaDetalhes.provinciaEntrega}`}
                                            {guiaDetalhes.contactoEntrega && ` · ${guiaDetalhes.contactoEntrega}`}
                                        </span>
                                    </div>
                                </div>

                                <div className="pdf-info-row transporte-row">
                                    <div className="pdf-info-item">
                                        <span className="pdf-info-label">Transportadora</span>
                                        <span className="pdf-info-value">{guiaDetalhes.transportadora || '—'}</span>
                                    </div>
                                    <div className="pdf-info-item">
                                        <span className="pdf-info-label">Motorista</span>
                                        <span className="pdf-info-value">
                                            {guiaDetalhes.motorista || '—'}
                                            {guiaDetalhes.documentoMotorista && (
                                                <span style={{
                                                    display: 'block',
                                                    fontSize: '0.55rem',
                                                    color: '#6b7280',
                                                    fontWeight: 400,
                                                    marginTop: '2px'
                                                }}>
                                                    Doc: {guiaDetalhes.documentoMotorista}
                                                </span>
                                            )}
                                        </span>
                                    </div>
                                    <div className="pdf-info-item">
                                        <span className="pdf-info-label">Matrícula</span>
                                        <span className="pdf-info-value">{guiaDetalhes.matricula || '—'}</span>
                                    </div>
                                </div>

                                {guiaDetalhes.status === 'Entregue' && guiaDetalhes.recebidoPor && (
                                    <div className="pdf-info-row" style={{
                                        background: 'linear-gradient(135deg, rgba(5, 150, 105, 0.06), rgba(5, 150, 105, 0.02))',
                                        border: '1px solid rgba(5, 150, 105, 0.25)',
                                        borderLeft: '3px solid #059669'
                                    }}>
                                        <div className="pdf-info-item">
                                            <span className="pdf-info-label" style={{ color: '#059669' }}>Recebido por</span>
                                            <span className="pdf-info-value">{guiaDetalhes.recebidoPor}</span>
                                        </div>
                                        <div className="pdf-info-item">
                                            <span className="pdf-info-label" style={{ color: '#059669' }}>Data de Entrega</span>
                                            <span className="pdf-info-value">
                                                {guiaDetalhes.dataEntrega ? formatarData(guiaDetalhes.dataEntrega) : '—'}
                                            </span>
                                        </div>
                                        <div className="pdf-info-item">
                                            <span className="pdf-info-label" style={{ color: '#059669' }}>Status</span>
                                            <span className="pdf-info-value">Entregue</span>
                                        </div>
                                    </div>
                                )}

                                <div className="pdf-itens-table-wrapper">
                                    <table className="pdf-itens-table">
                                        <thead>
                                            <tr>
                                                <th className="col-ordem">#</th>
                                                <th className="col-codigo">Cod</th>
                                                <th className="col-nome">Nome</th>
                                                <th className="col-descricao">Descrição</th>
                                                <th className="col-quantidade">Qtd.</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(guiaDetalhes.itens || []).map((it, i) => (
                                                <tr key={i}>
                                                    <td className="col-ordem">{i + 1}</td>
                                                    <td className="col-codigo">{it.codigoItem || '---'}</td>
                                                    <td className="col-nome">{it.nomeItem || '—'}</td>
                                                    <td className="col-descricao">{it.descricao || '-'}</td>
                                                    <td className="col-quantidade">{it.quantidade}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {guiaDetalhes.observacoes && (
                                    <div className="pdf-observacoes">
                                        <div className="pdf-observacoes-label">Observações</div>
                                        <div className="pdf-observacoes-text">{guiaDetalhes.observacoes}</div>
                                    </div>
                                )}

                                {guiaDetalhes.status === 'Anulada' && guiaDetalhes.motivoAnulacao && (
                                    <div className="pdf-observacoes" style={{ borderLeftColor: '#dc2626', background: '#fef2f2' }}>
                                        <div className="pdf-observacoes-label" style={{ color: '#dc2626' }}>Motivo da Anulação</div>
                                        <div className="pdf-observacoes-text" style={{ color: '#4a5568' }}>{guiaDetalhes.motivoAnulacao}</div>
                                    </div>
                                )}

                                {guiaDetalhes.tipoEntrega === 'Parcial'
                                    && (guiaDetalhes.itensRemovidos?.length || 0) > 0
                                    && guiaDetalhes.status !== 'Anulada'
                                    && guiaDetalhes.status !== 'Entregue' && (
                                        <RemovidosGrid
                                            itens={guiaDetalhes.itensRemovidos || []}
                                            title="Itens Removidos"
                                        />
                                    )}

                                <div className="pdf-assinaturas">
                                    <div className="assinaturaEntregue">
                                        Entregue por {guiaDetalhes.motorista}

                                    </div>
                                    <div className="assinaturaRecebido">
                                        Recebido por {guiaDetalhes.recebidoPor}

                                    </div>
                                </div>

                                <div className="pdf-footer-bottom">
                                    <div className="pdf-footer-info">
                                        <div style={{ textAlign: 'left' }}>
                                            <span>Documento emitido pelo Software RAF ERP</span>
                                        </div>
                                        <div style={{ textAlign: 'right' }}>
                                            <span>Utilizador: <strong>{guiaDetalhes.criadoPor?.nome || '—'}</strong></span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* PAINEL DIREITO — AÇÕES */}
                        <div className="modal-side-panel">
                            <div className="side-panel-header">
                                <h4><Icon name="settings" size={18} /> Ações</h4>
                            </div>
                            <div className="side-panel-body">
                                <div className="side-panel-status">
                                    <span className="status-label">Status</span>
                                    <span className={`status-badge ${(guiaDetalhes.status || '').toLowerCase()}`}>
                                        <Icon name={guiaDetalhes.statusIcon || getStatusInfo(guiaDetalhes.status).icon} size={12} />
                                        {guiaDetalhes.statusFormatado || guiaDetalhes.status}
                                    </span>
                                </div>

                                {guiaDetalhes.guiaOrigemId && (
                                    <>
                                        <div className="side-panel-divider"></div>
                                        <button
                                            className="side-panel-btn"
                                            onClick={() => rastrearPar(guiaDetalhes, 'origem')}
                                            style={{
                                                background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
                                                color: '#ffffff'
                                            }}
                                        >
                                            <Icon name="description" size={18} />
                                            <span style={{ fontSize: '0.75rem' }}>Ver Origem ({guiaDetalhes.guiaOrigemId?.numero || '?'})</span>
                                        </button>
                                    </>
                                )}

                                {(guiaDetalhes.guiasComplementaresIds?.length || 0) > 0 && (
                                    <>
                                        <div className="side-panel-divider"></div>
                                        {guiaDetalhes.guiasComplementaresIds.map((comp, idx) => {
                                            const compId = comp._id || comp;
                                            const compNum = comp.numero || '?';
                                            const total = guiaDetalhes.guiasComplementaresIds.length;
                                            const podeApagar = guiaDetalhes.status !== 'Entregue'
                                                && guiaDetalhes.status !== 'Anulada';
                                            return (
                                                <div
                                                    key={`side-comp-${idx}`}
                                                    style={{
                                                        display: 'flex',
                                                        gap: '0.35rem',
                                                        marginBottom: '0.4rem'
                                                    }}
                                                >
                                                    <button
                                                        className="side-panel-btn"
                                                        onClick={() => rastrearPar(guiaDetalhes, 'complementar', compId)}
                                                        style={{
                                                            flex: 1,
                                                            background: 'linear-gradient(135deg, #10b981, #059669)',
                                                            color: '#ffffff',
                                                            marginBottom: 0
                                                        }}
                                                    >
                                                        <Icon name="description" size={18} />
                                                        <span style={{ fontSize: '0.75rem' }}>
                                                            Ver Complementar {total > 1 ? `${idx + 1}/${total}` : ''} ({compNum})
                                                        </span>
                                                    </button>

                                                    {podeApagar && (
                                                        <button
                                                            className="side-panel-btn"
                                                            onClick={() => abrirApagarComplementares(guiaDetalhes, compId)}
                                                            title={`Apagar complementar ${compNum}`}
                                                            style={{
                                                                flex: '0 0 auto',
                                                                background: 'rgba(220, 38, 38, 0.1)',
                                                                color: '#dc2626',
                                                                border: '1px solid rgba(220, 38, 38, 0.3)',
                                                                padding: '0 12px',
                                                                width: '40px',
                                                                borderRadius: '10px',
                                                                marginBottom: 0
                                                            }}
                                                        >
                                                            <Icon name="delete" size={16} />
                                                        </button>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </>
                                )}

                                {guiaDetalhes.status === 'Anulada' && guiaDetalhes.motivoAnulacao && (
                                    <>
                                        <div className="side-panel-divider"></div>
                                        <div className="side-panel-cancel-motivo">
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                                                <Icon name="cancel" size={16} color="#dc2626" />
                                                <span style={{
                                                    fontSize: '0.75rem', fontWeight: 700,
                                                    color: '#dc2626', textTransform: 'uppercase'
                                                }}>Motivo da Anulação</span>
                                            </div>
                                            <p style={{
                                                margin: 0, fontSize: '0.85rem', color: '#1a1a2e',
                                                lineHeight: 1.5, wordBreak: 'break-word', whiteSpace: 'pre-wrap'
                                            }}>{guiaDetalhes.motivoAnulacao}</p>
                                        </div>
                                    </>
                                )}

                                {/* 🔥 Editar — bloqueado se bloqueada */}
                                {guiaDetalhes.status === 'Emitida' && !guiaDetalhes.bloqueadaParaEdicao && (
                                    <>
                                        <div className="side-panel-divider"></div>
                                        <button className="side-panel-btn btn-edit"
                                            onClick={() => { abrirEditar(guiaDetalhes); setShowDetailsModal(false); }}
                                            style={{
                                                background: 'linear-gradient(135deg, #4f46e5, #4338ca)',
                                                color: '#ffffff'
                                            }}>
                                            <Icon name="edit" size={18} />
                                            <span>Editar Guia</span>
                                        </button>
                                    </>
                                )}
                                {guiaDetalhes.status === 'Emitida' && guiaDetalhes.bloqueadaParaEdicao && (
                                    <>
                                        <div className="side-panel-divider"></div>
                                        <button
                                            className="side-panel-btn"
                                            onClick={() => showToast('Esta guia tem complementar associada. Apague a complementar primeiro.', 'warning')}
                                            disabled
                                            title="Bloqueada"
                                            style={{
                                                background: 'rgba(107, 114, 128, 0.15)',
                                                color: '#6b7280',
                                                cursor: 'not-allowed'
                                            }}
                                        >
                                            <Icon name="lock" size={18} />
                                            <span>Editar (Bloqueada)</span>
                                        </button>
                                    </>
                                )}

                                {/* 🔥 Entregar — bloqueado se tem removidos e não atingiu limite */}
                                {guiaDetalhes.status === 'Emitida' && (() => {
                                    const temRemovidos = (guiaDetalhes.itensRemovidos?.length || 0) > 0;
                                    const qtdComps = (guiaDetalhes.guiasComplementaresIds?.length || 0);
                                    const bloquear = temRemovidos && qtdComps < MAX_COMPLEMENTARES;

                                    return (
                                        <>
                                            <div className="side-panel-divider"></div>
                                            <button
                                                className="side-panel-btn btn-approve"
                                                onClick={() => {
                                                    if (bloquear) return;
                                                    abrirEntregar(guiaDetalhes);
                                                    setShowDetailsModal(false);
                                                }}
                                                disabled={bloquear || loadingEntregar}  // 🔥 CORRIGIDO
                                                title={bloquear
                                                    ? `Gere mais uma complementar antes de entregar (${qtdComps}/${MAX_COMPLEMENTARES})`
                                                    : 'Marcar como Entregue'}
                                                style={{
                                                    opacity: bloquear ? 0.45 : 1,
                                                    cursor: bloquear ? 'not-allowed' : 'pointer',
                                                    filter: bloquear ? 'grayscale(0.5)' : 'none'
                                                }}
                                            >
                                                <Icon name={bloquear ? 'lock' : 'delivery_truck'} size={18} />
                                                <span>
                                                    {loadingEntregar
                                                        ? 'A entregar...'
                                                        : bloquear
                                                            ? `Entregar (bloqueado ${qtdComps}/${MAX_COMPLEMENTARES})`
                                                            : 'Marcar Entregue'}
                                                </span>
                                            </button>

                                            {bloquear && (
                                                <div style={{
                                                    marginTop: '0.5rem',
                                                    padding: '0.5rem 0.75rem',
                                                    background: '#fffbeb',
                                                    border: '1px solid rgba(245, 158, 11, 0.3)',
                                                    borderLeft: '3px solid #f59e0b',
                                                    borderRadius: '6px',
                                                    fontSize: '0.7rem',
                                                    color: '#92400e',
                                                    display: 'flex',
                                                    alignItems: 'flex-start',
                                                    gap: '6px'
                                                }}>
                                                    <Icon name="info" size={14} style={{ flexShrink: 0, marginTop: '1px' }} />
                                                    <span>
                                                        Existem <strong>{guiaDetalhes.itensRemovidos.length} itens removidos</strong>.
                                                        Gere a complementar <strong>{qtdComps + 1}/{MAX_COMPLEMENTARES}</strong> para poder marcar como entregue.
                                                    </span>
                                                </div>
                                            )}
                                        </>
                                    );
                                })()}

                                {/* 🔥 Gerar Complementar — usa loadingComplementar */}
                                {guiaDetalhes.tipoEntrega === 'Parcial'
                                    && (guiaDetalhes.itensRemovidos?.length || 0) > 0
                                    && guiaDetalhes.status !== 'Anulada'
                                    && guiaDetalhes.status !== 'Entregue'
                                    && (guiaDetalhes.guiasComplementaresIds?.length || 0) < MAX_COMPLEMENTARES && (
                                        <>
                                            <div className="side-panel-divider"></div>
                                            <button
                                                className="side-panel-btn"
                                                onClick={() => abrirComplementar(guiaDetalhes)}
                                                style={{
                                                    background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                                                    color: '#ffffff'
                                                }}
                                                disabled={loadingComplementar}  // 🔥 CORRIGIDO
                                            >
                                                <Icon name="add_box" size={18} />
                                                <span>
                                                    {loadingComplementar
                                                        ? 'A criar...'
                                                        : `Gerar Complementar ${(guiaDetalhes.guiasComplementaresIds?.length || 0) + 1}/${MAX_COMPLEMENTARES} (${guiaDetalhes.itensRemovidos.length} itens)`}
                                                </span>
                                            </button>
                                        </>
                                    )}

                                {/* 🔥 Apagar Complementares (TODAS) — usa loadingApagarComps */}
                                {(guiaDetalhes.guiasComplementaresIds?.length || 0) > 0
                                    && guiaDetalhes.status !== 'Entregue'
                                    && guiaDetalhes.status !== 'Anulada' && (
                                        <>
                                            <div className="side-panel-divider"></div>
                                            <button
                                                className="side-panel-btn"
                                                onClick={() => abrirApagarComplementares(guiaDetalhes)}
                                                style={{
                                                    background: 'linear-gradient(135deg, #dc2626, #b91c1c)',
                                                    color: '#ffffff'
                                                }}
                                                disabled={loadingApagarComps}  // 🔥 CORRIGIDO
                                            >
                                                <Icon name="delete_sweep" size={18} />
                                                <span style={{ fontSize: '0.75rem' }}>
                                                    {loadingApagarComps
                                                        ? 'A apagar...'
                                                        : `Apagar Todas as Complementares (${guiaDetalhes.guiasComplementaresIds.length})`}
                                                </span>
                                            </button>
                                        </>
                                    )}

                                <div className="side-panel-divider"></div>

                                {/* 🔥 Baixar PDF — usa loadingPDF */}
                                <button
                                    className="side-panel-btn btn-pdf"
                                    onClick={() => baixarPDF(guiaDetalhes)}
                                    disabled={loadingPDF || guiaDetalhes.status === 'Anulada'}  // 🔥 CORRIGIDO
                                    title={guiaDetalhes.status === 'Anulada'
                                        ? 'Guia anulada — exportação indisponível'
                                        : 'Baixar PDF'}
                                    style={{
                                        opacity: guiaDetalhes.status === 'Anulada' ? 0.5 : 1,
                                        cursor: guiaDetalhes.status === 'Anulada' ? 'not-allowed' : 'pointer'
                                    }}
                                >
                                    <Icon name="picture_as_pdf" size={18} />
                                    <span>{loadingPDF ? 'A gerar...' : 'Baixar PDF'}</span>
                                </button>

                                {/* 🔥 Imprimir — usa loadingPrint */}
                                <button
                                    className="side-panel-btn btn-print"
                                    onClick={() => imprimirGuia(guiaDetalhes)}
                                    disabled={loadingPrint || guiaDetalhes.status === 'Anulada'}  // 🔥 CORRIGIDO
                                    title={guiaDetalhes.status === 'Anulada'
                                        ? 'Guia anulada — impressão indisponível'
                                        : 'Imprimir'}
                                    style={{
                                        opacity: guiaDetalhes.status === 'Anulada' ? 0.5 : 1,
                                        cursor: guiaDetalhes.status === 'Anulada' ? 'not-allowed' : 'pointer'
                                    }}
                                >
                                    <Icon name="print" size={18} />
                                    <span>{loadingPrint ? 'A preparar...' : 'Imprimir'}</span>
                                </button>

                                {/* 🔥 Anular — bloqueado se tiver complementares */}
                                {guiaDetalhes.status !== 'Anulada'
                                    && guiaDetalhes.status !== 'Entregue'
                                    && (guiaDetalhes.guiasComplementaresIds?.length || 0) === 0 && (
                                        <button className="side-panel-btn btn-cancel-action"
                                            onClick={() => {
                                                setGuiaToAnular(guiaDetalhes);
                                                setShowDetailsModal(false);
                                                setMotivoAnulacao('');
                                                setShowAnularModal(true);
                                            }}>
                                            <Icon name="cancel" size={18} />
                                            <span>Anular Guia</span>
                                        </button>
                                    )}

                                {guiaDetalhes.status !== 'Anulada'
                                    && guiaDetalhes.status !== 'Entregue'
                                    && (guiaDetalhes.guiasComplementaresIds?.length || 0) > 0 && (
                                        <button
                                            className="side-panel-btn btn-cancel-action"
                                            onClick={() => showToast('Apague primeiro as complementares antes de anular.', 'warning')}
                                            disabled
                                            title="Bloqueada"
                                            style={{ opacity: 0.5, cursor: 'not-allowed' }}
                                        >
                                            <Icon name="lock" size={18} />
                                            <span>Anular (Bloqueado)</span>
                                        </button>
                                    )}

                                <div className="side-panel-divider"></div>
                                <button className="side-panel-btn btn-close" onClick={() => setShowDetailsModal(false)}>
                                    <Icon name="close" size={18} />
                                    <span>Fechar</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* MODAIS DE CONFIRMAÇÃO */}
            {showEntregarModal && guiaToEntregar && (
                <div className="modal-overlay" onClick={() => setShowEntregarModal(false)}>
                    <div className="modal-content-popups-cotacao modal-confirm" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3><span className="header-icon"></span> Marcar como Entregue</h3>
                            <button className="modal-close" onClick={() => setShowEntregarModal(false)}>×</button>
                        </div>
                        <div className="modal-body">
                            <div className="confirm-icon success">
                                <Icon name="check_circle" size={32} />
                            </div>
                            <h4>Confirmar Entrega</h4>
                            <p>Confirmar entrega da guia <strong>{guiaToEntregar.numero}</strong>?</p>
                            <p className="confirm-subtext" style={{ marginTop: 6, textAlign: 'left' }}>
                                <Icon name="info" size={14} />
                                Ao confirmar, o stock dos produtos será reduzido automaticamente.
                            </p>
                            {(guiaToEntregar.itensRemovidos?.length || 0) > 0 && (
                                <p className="confirm-subtext" style={{
                                    marginTop: 8, padding: '6px 10px',
                                    background: '#fffbeb',
                                    border: '1px solid rgba(245, 158, 11, 0.3)',
                                    borderRadius: '6px',
                                    color: '#92400e'
                                }}>
                                    <Icon name="warning" size={14} />
                                    {' '}Existem <strong>{guiaToEntregar.itensRemovidos.length} item(ns) removido(s)</strong> — considere gerar guia complementar.
                                </p>
                            )}
                            <div className="form-group-cancelamento" style={{ marginTop: 12 }}>
                                <label>Recebido por</label>
                                <input type="text" value={recebidoPor}
                                    onChange={e => setRecebidoPor(e.target.value)}
                                    placeholder="Nome de quem recebeu" autoFocus
                                    style={{
                                        width: '100%', padding: '0.6rem 0.8rem',
                                        border: '1.5px solid #e5e7eb', borderRadius: '8px',
                                        fontSize: '0.85rem', outline: 'none'
                                    }} />
                            </div>
                        </div>
                        <div className="modal-actions">
                            <button className="btn-confirm-success" onClick={confirmarEntregar} disabled={loadingEntregar}>
                                {loadingEntregar ? 'A processar...' : 'Confirmar Entrega'}
                            </button>
                            <button className="btn-cancel" onClick={() => setShowEntregarModal(false)}>Cancelar</button>
                        </div>
                    </div>
                </div>
            )}

            {showComplementarModal && guiaToComplementar && (() => {
                const qtdAtual = guiaToComplementar.guiasComplementaresIds?.length || 0;
                const proxima = qtdAtual + 1;
                const isUltima = proxima >= MAX_COMPLEMENTARES;
                const qtdItens = guiaToComplementar.itensRemovidos?.length || 0;

                return (
                    <div className="modal-overlay" onClick={() => setShowComplementarModal(false)}>
                        <div
                            className="modal-content-popups-cotacao modal-confirm"
                            onClick={e => e.stopPropagation()}
                            style={{ maxWidth: '640px' }}
                        >
                            <div className="modal-header">
                                <h3>
                                    <span className="header-icon">📦</span> Gerar Guia Complementar
                                </h3>
                                <button className="modal-close" onClick={() => setShowComplementarModal(false)}>×</button>
                            </div>

                            <div className="modal-body">
                                <div className="confirm-icon" style={{ color: '#f59e0b' }}>
                                    <Icon name="add_box" size={32} />
                                </div>

                                <h4>Criar Guia Complementar {proxima}/{MAX_COMPLEMENTARES}</h4>

                                <p>
                                    Será criada uma nova guia com o <strong>restante do material</strong> removido da
                                    guia <strong>{guiaToComplementar.numero}</strong>.
                                </p>

                                {isUltima && (
                                    <p
                                        className="confirm-subtext"
                                        style={{
                                            marginTop: 8,
                                            padding: '8px 12px',
                                            background: '#fffbeb',
                                            border: '1px solid rgba(245, 158, 11, 0.35)',
                                            borderLeft: '3px solid #f59e0b',
                                            borderRadius: '8px',
                                            color: '#92400e',
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            gap: '8px',
                                            textAlign: 'left'
                                        }}
                                    >
                                        <Icon name="warning" size={16} style={{ flexShrink: 0, marginTop: '1px' }} />
                                        <span>
                                            Esta é a <strong>última complementar permitida</strong> ({proxima}/{MAX_COMPLEMENTARES}).
                                            Após esta, não será possível gerar mais guias complementares para esta fatura.
                                        </span>
                                    </p>
                                )}

                                <div
                                    style={{
                                        marginTop: 14,
                                        width: 'auto',
                                        display: 'flex',
                                        justifyContent: 'flex-start'
                                    }}
                                >
                                    <RemovidosGrid
                                        itens={guiaToComplementar.itensRemovidos || []}
                                        title={`Itens que vão para a complementar`}
                                    />
                                </div>
                            </div>

                            <div className="modal-actions">
                                <button
                                    className="btn-confirm-success"
                                    onClick={confirmarComplementar}
                                    disabled={loadingComplementar}
                                    style={{
                                        background: 'linear-gradient(135deg, #f59e0b, #d97706)'
                                    }}
                                >
                                    {loadingComplementar ? 'A criar...' : `Confirmar e Criar (${proxima}/${MAX_COMPLEMENTARES})`}
                                </button>
                                <button
                                    className="btn-cancel"
                                    onClick={() => setShowComplementarModal(false)}
                                    disabled={loadingComplementar}
                                >
                                    Cancelar
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* 🔥 MODAL — Apagar Complementares */}
            {showApagarComplementaresModal && guiaToApagarComplementares && (
                <div className="modal-overlay" onClick={() => setShowApagarComplementaresModal(false)}>
                    <div className="modal-content-popups-cotacao modal-confirm" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3><span className="header-icon"></span> Apagar Complementares</h3>
                            <button className="modal-close" onClick={() => setShowApagarComplementaresModal(false)}>×</button>
                        </div>
                        <div className="modal-body">
                            <div className="confirm-icon danger">
                                <Icon name="warning" size={32} />
                            </div>
                            <h4>Apagar {complementarIdParaApagar ? 'complementar' : 'TODAS as complementares'}?</h4>
                            <p style={{ textAlign: 'center' }}>
                                {complementarIdParaApagar
                                    ? <>A complementar selecionada vai ser apagada e os <strong>itens vão voltar</strong> para a guia <strong>{guiaToApagarComplementares.numero}</strong>.</>
                                    : <><strong>{guiaToApagarComplementares.guiasComplementaresIds?.length || 0} complementar(es)</strong> vão ser apagadas e os <strong>itens vão voltar</strong> para a guia <strong>{guiaToApagarComplementares.numero}</strong>.</>
                                }
                            </p>
                            <p className="confirm-subtext" style={{ marginTop: 8, textAlign: 'left' }}>
                                <Icon name="info" size={14} />
                                A guia original ficará desbloqueada para edição após esta ação.
                            </p>
                        </div>
                        <div className="modal-actions">
                            <button className="btn-confirm-danger" onClick={confirmarApagarComplementares} disabled={loadingApagarComps}>
                                {loadingApagarComps ? 'A apagar...' : 'Sim, Apagar'}
                            </button>
                            <button className="btn-cancel" onClick={() => setShowApagarComplementaresModal(false)}>Cancelar</button>
                        </div>
                    </div>
                </div>
            )}

            {showAnularModal && guiaToAnular && (
                <div className="modal-overlay" onClick={() => setShowAnularModal(false)}>
                    <div className="modal-content-popups-cotacao modal-confirm" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3><span className="header-icon">❌</span> Anular Guia</h3>
                            <button className="modal-close" onClick={() => setShowAnularModal(false)}>×</button>
                        </div>
                        <div className="modal-body">
                            <div className="confirm-icon danger">
                                <Icon name="warning" size={32} />
                            </div>
                            <h4>Confirmar Anulação</h4>
                            <p>Tem certeza que deseja anular a guia <strong>{guiaToAnular.numero}</strong>?</p>
                            <div className="form-group-cancelamento">
                                <label>Motivo da anulação <span className="required">*</span></label>
                                <textarea value={motivoAnulacao}
                                    onChange={e => setMotivoAnulacao(e.target.value)}
                                    rows="3" placeholder="Informe o motivo..."
                                    style={{
                                        width: '100%', padding: '0.6rem 0.8rem',
                                        border: '1.5px solid #e5e7eb', borderRadius: '8px',
                                        fontSize: '0.85rem', outline: 'none',
                                        fontFamily: 'inherit', resize: 'vertical', minHeight: '60px'
                                    }} />
                            </div>
                        </div>
                        <div className="modal-actions">
                            <button className="btn-confirm-danger" onClick={anularGuia} disabled={loading}>
                                {loading ? 'A processar...' : 'Sim, Anular'}
                            </button>
                            <button className="btn-cancel" onClick={() => setShowAnularModal(false)}>Cancelar</button>
                        </div>
                    </div>
                </div>
            )}

            {showDeleteModal && guiaToDelete && (
                <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
                    <div className="modal-content-popups-cotacao modal-confirm" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3><span className="header-icon">⚠️</span> Confirmar Exclusão</h3>
                            <button className="modal-close" onClick={() => setShowDeleteModal(false)}>×</button>
                        </div>
                        <div className="modal-body">
                            <div className="confirm-icon danger">
                                <Icon name="warning" size={32} />
                            </div>
                            <h4>Remover guia?</h4>
                            <p>Tem certeza que deseja remover a guia <strong>{guiaToDelete.numero}</strong>?</p>
                            <p className="confirm-subtext">
                                <Icon name="info" size={14} /> Esta ação é irreversível.
                            </p>
                        </div>
                        <div className="modal-actions">
                            <button className="btn-confirm-danger" onClick={excluirGuia} disabled={loading}>
                                {loading ? 'A processar...' : 'Eliminar'}
                            </button>
                            <button className="btn-cancel" onClick={() => setShowDeleteModal(false)}>Cancelar</button>
                        </div>
                    </div>
                </div>
            )}

            <Toast message={toast.message} type={toast.type} onClose={closeToast} />
        </div>
    );
}